import { create } from 'zustand';
import {
  KEY_RECOIL,
  REVIEWED_BEAT_FILTER,
  SORT_BEATS_OPTIONS,
  TEMPLATE_SOURCE_OPTIONS,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  TYPE_SPECIAL_EVENT_ENUM,
  DEVICE_EVENT_TAB_ENUM,
  SIZE_DEVICE_STRIP_MANAGEMENT_THUMBNAIL,
  SIZE_STRIP_MANAGEMENT_THUMBNAIL,
} from '@/shared/config';
import {
  JUMP_DATE_STATUS,
  BULK_ACTION_ENUM,
  DATA_HEATMAP_STATUS,
  LOAD_BEAT_STATUS,
  SIZE_THUMBNAIL_OPTIONS,
  TBeatHrActiveTab,
  type IDailySummary,
  type IHrSummary,
  type IHrsInDate,
  type IControlModalHrSummary,
  type IHrChartData,
  type TJumpDateStatus,
  type DataHeatmapChart,
  type IListSelectedCells,
  type ILoadBeatStatus,
  type TBulkAction,
  type TDataHeatmapChart,
  type TDataHeatmapStatus,
  type TRrOverview,
  type IEcgDisclosure,
  type IDeviceEventCount,
  type IDeviceEventData,
  type ISdeviceStripFilter,
  type IStripFilter,
  type IBeat,
  type IBeatChange,
  type IControlModal,
  type IDailyCount,
  type IEcgBookmark,
  type IEvent,
  type IEventOther,
  type IPageIndex,
  type IPoint,
  type IReportInfo,
  type ISizeThumbnailConfig,
  type TAiChannel,
  type TPickDateAction,
  type IBeatEventCount,
  type IEventFilter,
  type IGroupEventChanges,
  type IRhythmEventCount,
  type ISpecialEventTypes,
} from '@/shared/model';
import {
  createFieldPersistence,
  createKeyedFieldPersistence,
  loadPersistedKeyedRecord,
  loadPersistedValue,
} from '@/shared/lib';
import { generateDefaultHeatmapState } from '@/shared/lib/heatmap';
import {
  SIZE_BEAT_THUMBNAIL,
  SIZE_EVENT_THUMBNAIL,
  defaultControlModalValue,
  defaultEventOptions,
} from './reportDefaults';

const ALL_TAB_KEYS = Object.values(KEY_RECOIL);

type Setter<T> = T | ((prev: T) => T);
/**
 * Normalizes a store setter argument into the concrete value to store. Invokes next with the previous value when next is a function, otherwise passes next straight through.
 *
 * @param next - The replacement value, or an updater receiving the previous value.
 * @param prev - The current value, handed to next when it is an updater.
 * @returns The resolved next value.
 */
const resolve = <T>(next: Setter<T>, prev: T): T =>
  typeof next === 'function' ? (next as (p: T) => T)(prev) : next;

type ViewHrMode = TYPE_RHYTHM_EVENT_ENUM.MAX_HR | TYPE_RHYTHM_EVENT_ENUM.MIN_HR;

/** Same literal as v1's `hrDataInitial`. */
const HR_DATA_INITIAL: IHrSummary = {
  maxHr: { value: undefined, time: undefined },
  minHr: { value: undefined, time: undefined },
  avgHr: { value: undefined },
};

const VIEW_HR_MODE_DEFAULT = TYPE_RHYTHM_EVENT_ENUM.MIN_HR;
const IS_VIEW_NEW_EVENT_DEFAULT = false;

/**
 * Reads the heart-rate view mode of one report tab out of the combined report store. Indexes s.viewHrMode by the given key and falls back to VIEW_HR_MODE_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectViewHrMode = (key: string) => (s: ReportState) =>
  s.viewHrMode[key] ?? VIEW_HR_MODE_DEFAULT;
/**
 * Reads the heart-rate time selected on one report tab. Indexes s.selectedHrTime by the given key with no fallback, so a tab that has never set one reads as undefined.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSelectedHrTime = (key: string) => (s: ReportState) => s.selectedHrTime[key];
/**
 * Reads whether one report tab is showing newly arrived events out of the combined report store. Indexes s.isViewNewEvent by the given key and falls back to IS_VIEW_NEW_EVENT_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsViewNewEvent = (key: string) => (s: ReportState) =>
  s.isViewNewEvent[key] ?? IS_VIEW_NEW_EVENT_DEFAULT;

const JUMP_DATE_STATUS_DEFAULT = JUMP_DATE_STATUS.NORMAL;
// Absent-key fallbacks for objects/arrays MUST be module-level frozen constants (F18).
const EMPTY_CHART_HRS_DATA = Object.freeze([]) as unknown as Array<IHrChartData>;
const EMPTY_EVENT_STRIPS_DATA = Object.freeze([]) as unknown as Array<any>;
const DEVICE_EVENT_DATA_DEFAULT: IDeviceEventData = Object.freeze({
  notificationEvents: [],
  patientTriggeredEvents: [],
  otherEvents: [],
});
const EMPTY_CHANGE_STRIP_DATA = Object.freeze([]) as unknown as Array<any>;
const STRIP_FILTER_DEFAULT: IStripFilter = Object.freeze({
  isHideExcludeReport: false,
  showCaptureType: 'ALL',
});
const DEVICE_STRIP_FILTER_DEFAULT: ISdeviceStripFilter = Object.freeze({
  eventFid: undefined,
  startSearchDate: undefined,
  stopSearchDate: undefined,
  eventType: undefined,
  isValid: undefined,
  order: 'asc',
});
const EMPTY_SELECTED_KEY_DEFAULT = Object.freeze({});
const RELOAD_STRIP_MANAGEMENT_DEFAULT = 0;
const DEVICE_EVENT_COUNT_DEFAULT: IDeviceEventCount = Object.freeze({
  notificationEvents: 0,
  patientTriggeredEvents: 0,
  otherEvents: 0,
});
const IS_WAITING_DEFAULT = false;

/**
 * Reads the status of the pending date jump on one report tab out of the combined report store. Indexes s.jumpDateStatus by the given key and falls back to JUMP_DATE_STATUS_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectJumpDateStatus = (key: string) => (s: ReportState) =>
  s.jumpDateStatus[key] ?? JUMP_DATE_STATUS_DEFAULT;
/**
 * Reads the heart-rate chart series of one report tab out of the combined report store. Indexes s.chartHrsData by the given key and falls back to EMPTY_CHART_HRS_DATA when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectChartHrsData = (key: string) => (s: ReportState) =>
  s.chartHrsData[key] ?? EMPTY_CHART_HRS_DATA;
/**
 * Reads the event strip data of one report tab out of the combined report store. Indexes s.eventStripsData by the given key and falls back to EMPTY_EVENT_STRIPS_DATA when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectEventStripsData = (key: string) => (s: ReportState) =>
  s.eventStripsData[key] ?? EMPTY_EVENT_STRIPS_DATA;
/**
 * Reads the device event data of one report tab out of the combined report store. Indexes s.deviceEventData by the given key and falls back to DEVICE_EVENT_DATA_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectDeviceEventData = (key: string) => (s: ReportState) =>
  s.deviceEventData[key] ?? DEVICE_EVENT_DATA_DEFAULT;
/**
 * Reads the pending strip edits of one report tab out of the combined report store. Indexes s.changeStripData by the given key and falls back to EMPTY_CHANGE_STRIP_DATA when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectChangeStripData = (key: string) => (s: ReportState) =>
  s.changeStripData[key] ?? EMPTY_CHANGE_STRIP_DATA;
/**
 * Reads the strip filter of one report tab out of the combined report store. Indexes s.stripFilter by the given key and falls back to STRIP_FILTER_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectStripFilter = (key: string) => (s: ReportState) =>
  s.stripFilter[key] ?? STRIP_FILTER_DEFAULT;
/**
 * Reads the device strip filter of one report tab out of the combined report store. Indexes s.deviceStripFilter by the given key and falls back to DEVICE_STRIP_FILTER_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectDeviceStripFilter = (key: string) => (s: ReportState) =>
  s.deviceStripFilter[key] ?? DEVICE_STRIP_FILTER_DEFAULT;
/**
 * Reads the empty-selection marker of one report tab out of the combined report store. Indexes s.emptySelectedKey by the given key and falls back to EMPTY_SELECTED_KEY_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectEmptySelectedKey = (key: string) => (s: ReportState) =>
  s.emptySelectedKey[key] ?? EMPTY_SELECTED_KEY_DEFAULT;
/**
 * Reads the strip management reload counter of one report tab out of the combined report store. Indexes s.reloadStripManagement by the given key and falls back to RELOAD_STRIP_MANAGEMENT_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectReloadStripManagement = (key: string) => (s: ReportState) =>
  s.reloadStripManagement[key] ?? RELOAD_STRIP_MANAGEMENT_DEFAULT;
/**
 * Reads the device event counts of one report tab out of the combined report store. Indexes s.deviceEventCount by the given key and falls back to DEVICE_EVENT_COUNT_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectDeviceEventCount = (key: string) => (s: ReportState) =>
  s.deviceEventCount[key] ?? DEVICE_EVENT_COUNT_DEFAULT;
/**
 * Reads whether one report tab is waiting on a pending operation out of the combined report store. Indexes s.isWaiting by the given key and falls back to IS_WAITING_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsWaiting = (key: string) => (s: ReportState) =>
  s.isWaiting[key] ?? IS_WAITING_DEFAULT;

const IS_SKIP_SELECT_THUMBNAIL_DEFAULT = false;
const EMPTY_DAILY_ARTIFACT_COUNT = Object.freeze([]) as unknown as Array<IDailyCount>;
const EMPTY_MORPHOLOGY_TEMPLATE = Object.freeze([]) as unknown as Array<any>;
const EMPTY_TEMPLATE_CHANGES = Object.freeze([]) as unknown as Array<any>;
const SELECTED_TEMPLATE_DEFAULT = Object.freeze({});
const EMPTY_SWAP_BEAT_CHANGES = Object.freeze([]) as unknown as Array<any>;
const EMPTY_BULK_SWAP_BEATS = Object.freeze([]) as unknown as Array<any>;
const EMPTY_RESET_SWAP_BEATS = Object.freeze([]) as unknown as Array<any>;
const BULK_ACTION_DEFAULT: TBulkAction = BULK_ACTION_ENUM.NOTHING;
const BULK_ACTION_SWAP_DEFAULT = Object.freeze({});

/**
 * Reads whether thumbnail auto-selection is skipped on one report tab out of the combined report store. Indexes s.isSkipSelectThumbnail by the given key and falls back to IS_SKIP_SELECT_THUMBNAIL_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsSkipSelectThumbnail = (key: string) => (s: ReportState) =>
  s.isSkipSelectThumbnail[key] ?? IS_SKIP_SELECT_THUMBNAIL_DEFAULT;
/**
 * Reads the per-day artifact counts of one report tab out of the combined report store. Indexes s.dailyArtifactCount by the given key and falls back to EMPTY_DAILY_ARTIFACT_COUNT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectDailyArtifactCount = (key: string) => (s: ReportState) =>
  s.dailyArtifactCount[key] ?? EMPTY_DAILY_ARTIFACT_COUNT;
/**
 * Reads the beat morphology templates of one report tab out of the combined report store. Indexes s.morphologyTemplate by the given key and falls back to EMPTY_MORPHOLOGY_TEMPLATE when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectMorphologyTemplate = (key: string) => (s: ReportState) =>
  s.morphologyTemplate[key] ?? EMPTY_MORPHOLOGY_TEMPLATE;
/**
 * Reads the pending morphology template edits of one report tab out of the combined report store. Indexes s.templateChanges by the given key and falls back to EMPTY_TEMPLATE_CHANGES when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectTemplateChanges = (key: string) => (s: ReportState) =>
  s.templateChanges[key] ?? EMPTY_TEMPLATE_CHANGES;
/**
 * Reads the morphology template selected on one report tab out of the combined report store. Indexes s.selectedTemplate by the given key and falls back to SELECTED_TEMPLATE_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSelectedTemplate = (key: string) => (s: ReportState) =>
  s.selectedTemplate[key] ?? SELECTED_TEMPLATE_DEFAULT;
/**
 * Reads the pending beat swaps of one report tab out of the combined report store. Indexes s.swapBeatChanges by the given key and falls back to EMPTY_SWAP_BEAT_CHANGES when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSwapBeatChanges = (key: string) => (s: ReportState) =>
  s.swapBeatChanges[key] ?? EMPTY_SWAP_BEAT_CHANGES;
/**
 * Reads the pending bulk action of one report tab out of the combined report store. Indexes s.bulkAction by the given key and falls back to BULK_ACTION_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectBulkAction = (key: string) => (s: ReportState) =>
  s.bulkAction[key] ?? BULK_ACTION_DEFAULT;
/**
 * Reads the bulk beat-swap action of one report tab out of the combined report store. Indexes s.bulkActionSwap by the given key and falls back to BULK_ACTION_SWAP_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectBulkActionSwap = (key: string) => (s: ReportState) =>
  s.bulkActionSwap[key] ?? BULK_ACTION_SWAP_DEFAULT;
/**
 * Reads the beats queued for a bulk swap on one report tab out of the combined report store. Indexes s.bulkSwapBeats by the given key and falls back to EMPTY_BULK_SWAP_BEATS when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectBulkSwapBeats = (key: string) => (s: ReportState) =>
  s.bulkSwapBeats[key] ?? EMPTY_BULK_SWAP_BEATS;
/**
 * Reads the beats to reset after a swap on one report tab out of the combined report store. Indexes s.resetSwapBeats by the given key and falls back to EMPTY_RESET_SWAP_BEATS when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectResetSwapBeats = (key: string) => (s: ReportState) =>
  s.resetSwapBeats[key] ?? EMPTY_RESET_SWAP_BEATS;
/**
 * Reads the per-cell bulk action of one report tab out of the combined report store. Indexes s.bulkActionByCell by the given key and falls back to BULK_ACTION_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectBulkActionByCell = (key: string) => (s: ReportState) =>
  s.bulkActionByCell[key] ?? BULK_ACTION_DEFAULT;

const REPORT_INFO_DEFAULT: IReportInfo | {} = Object.freeze({});
const LIMIT_PROCESSING_STUDY_DEFAULT = Object.freeze({});
const LIST_DOWNLOAD_FILE_DEFAULT = Object.freeze([]) as unknown as any;
const DATE_LIST_DEFAULT = Object.freeze([]) as unknown as Array<any>;
const SPECIAL_EVENT_TYPES_DEFAULT: ISpecialEventTypes = Object.freeze({
  TACHY: TYPE_SPECIAL_EVENT_ENUM.ALL_HR_RANGES,
  BRADY: TYPE_SPECIAL_EVENT_ENUM.ALL_HR_RANGES,
  PAUSE: TYPE_SPECIAL_EVENT_ENUM.ALL_HR_RANGES,
});

const CHANNELS_THUMBNAIL_DEFAULT = Object.freeze([4]) as unknown as Array<number>;
const AI_CHANNEL_DEFAULT: TAiChannel | null = null;
const ECG_OBJECT_DEFAULT = Object.freeze({});
const RELOAD_ECG_VIEWER_DEFAULT = 0;
const IS_LOADING_ECG_VIEWER_DEFAULT = false;
const PREVENT_RELOAD_ECG_DEFAULT = false;
const PICK_DATE_ACTION_DEFAULT: TPickDateAction | null = null;
const PICKED_START_DATE_DEFAULT: string | null = null;
const PICKED_END_DATE_DEFAULT: string | null = null;
const PICKED_BOOKMARK_DATE_DEFAULT: string | null = null;
const JUMP_DATE_DEFAULT: any = undefined;
const JUMP_TIME_DEFAULT: number | undefined = undefined;
const JUMP_PORTION_DEFAULT = 0;
const SELECTED_DATE_VALUE_DEFAULT: string | null = null;
const DAILY_COUNT_DEFAULT = Object.freeze([]) as unknown as Array<IDailyCount>;

/**
 * Reads the AI channel chosen for one report tab out of the combined report store. Indexes s.aiChannel by the given key and falls back to AI_CHANNEL_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectAiChannel = (key: string) => (s: ReportState) =>
  s.aiChannel[key] ?? AI_CHANNEL_DEFAULT;
/**
 * Reads the loaded ECG object of one report tab out of the combined report store. Indexes s.ecgObject by the given key and falls back to ECG_OBJECT_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectEcgObject = (key: string) => (s: ReportState) =>
  s.ecgObject[key] ?? ECG_OBJECT_DEFAULT;
/**
 * Reads the ECG viewer reload counter of one report tab out of the combined report store. Indexes s.reloadEcgViewer by the given key and falls back to RELOAD_ECG_VIEWER_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectReloadEcgViewer = (key: string) => (s: ReportState) =>
  s.reloadEcgViewer[key] ?? RELOAD_ECG_VIEWER_DEFAULT;
/**
 * Reads whether the ECG viewer of one report tab is loading out of the combined report store. Indexes s.isLoadingEcgViewer by the given key and falls back to IS_LOADING_ECG_VIEWER_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsLoadingEcgViewer = (key: string) => (s: ReportState) =>
  s.isLoadingEcgViewer[key] ?? IS_LOADING_ECG_VIEWER_DEFAULT;
/**
 * Reads whether an ECG reload is suppressed on one report tab out of the combined report store. Indexes s.preventReloadEcg by the given key and falls back to PREVENT_RELOAD_ECG_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectPreventReloadEcg = (key: string) => (s: ReportState) =>
  s.preventReloadEcg[key] ?? PREVENT_RELOAD_ECG_DEFAULT;
/**
 * Reads the pending date-picker action of one report tab out of the combined report store. Indexes s.pickDateAction by the given key and falls back to PICK_DATE_ACTION_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectPickDateAction = (key: string) => (s: ReportState) =>
  s.pickDateAction[key] ?? PICK_DATE_ACTION_DEFAULT;
/**
 * Reads the start date picked on one report tab out of the combined report store. Indexes s.pickedStartDate by the given key and falls back to PICKED_START_DATE_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectPickedStartDate = (key: string) => (s: ReportState) =>
  s.pickedStartDate[key] ?? PICKED_START_DATE_DEFAULT;
/**
 * Reads the end date picked on one report tab out of the combined report store. Indexes s.pickedEndDate by the given key and falls back to PICKED_END_DATE_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectPickedEndDate = (key: string) => (s: ReportState) =>
  s.pickedEndDate[key] ?? PICKED_END_DATE_DEFAULT;
/**
 * Reads the bookmark date picked on one report tab out of the combined report store. Indexes s.pickedBookmarkDate by the given key and falls back to PICKED_BOOKMARK_DATE_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectPickedBookmarkDate = (key: string) => (s: ReportState) =>
  s.pickedBookmarkDate[key] ?? PICKED_BOOKMARK_DATE_DEFAULT;
/**
 * Reads the date one report tab should jump to out of the combined report store. Indexes s.jumpDate by the given key and falls back to JUMP_DATE_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectJumpDate = (key: string) => (s: ReportState) =>
  s.jumpDate[key] ?? JUMP_DATE_DEFAULT;
/**
 * Reads the timestamp one report tab should jump to out of the combined report store. Indexes s.jumpTime by the given key and falls back to JUMP_TIME_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectJumpTime = (key: string) => (s: ReportState) =>
  s.jumpTime[key] ?? JUMP_TIME_DEFAULT;
/**
 * Reads the portion of the day one report tab should jump to out of the combined report store. Indexes s.jumpPortion by the given key and falls back to JUMP_PORTION_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectJumpPortion = (key: string) => (s: ReportState) =>
  s.jumpPortion[key] ?? JUMP_PORTION_DEFAULT;
/**
 * Reads the date selected on one report tab out of the combined report store. Indexes s.selectedDateValue by the given key and falls back to SELECTED_DATE_VALUE_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSelectedDateValue = (key: string) => (s: ReportState) =>
  s.selectedDateValue[key] ?? SELECTED_DATE_VALUE_DEFAULT;
/**
 * Reads the per-day event counts of one report tab out of the combined report store. Indexes s.dailyCount by the given key and falls back to DAILY_COUNT_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectDailyCount = (key: string) => (s: ReportState) =>
  s.dailyCount[key] ?? DAILY_COUNT_DEFAULT;

// v1's own generateDefaultState is typed `any` (Recoil/handler.ts) so this
// '' fallback for unmapped keys was never actually type-checked either.
/**
 * Picks the sub-tab a report tab starts on before the user has chosen one. Maps TAB_1 to the artifact rhythm event, TAB_2 to the VE couplet beat event and TAB_3 to the device notification tab, and yields an empty string for any other key.
 *
 * @param key - The report-tab store key whose starting sub-tab is wanted.
 * @returns The starting sub-tab for that tab, or an empty string for an unrecognised key.
 */
const activeTabDefault = (
  key: string,
): TBeatHrActiveTab | TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM => {
  if (key === KEY_RECOIL.BEAT_HR) return TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
  if (key === KEY_RECOIL.RHYTHM_EVENT) return TYPE_BEAT_EVENT_ENUM.VE_COUPLET;
  if (key === KEY_RECOIL.STRIP_MANAGEMENT) return DEVICE_EVENT_TAB_ENUM.NOTIFICATION as any;
  return '' as any;
};
const PAGE_INDEX_DEFAULT = Object.freeze({ index: 0 }) as unknown as IPageIndex;
const IS_ACTIVE_TAB_DEFAULT = false;
const IS_EMPTY_DATA_DEFAULT = false;
const RELOAD_HR_CHART_DEFAULT = 0;
const IS_LOADING_HR_CHART_DEFAULT = true;
const IS_LOADING_HR_CHART_OPTIONS_DEFAULT = false;

/**
 * Reads the sub-tab currently selected within one report tab. Indexes s.activeTab by the given key and falls back to that key's starting sub-tab from activeTabDefault when the tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectActiveTab = (key: string) => (s: ReportState) =>
  s.activeTab[key] ?? activeTabDefault(key);
/**
 * Reads the pagination position of one report tab out of the combined report store. Indexes s.pageIndex by the given key and falls back to PAGE_INDEX_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectPageIndex = (key: string) => (s: ReportState) =>
  s.pageIndex[key] ?? PAGE_INDEX_DEFAULT;
/**
 * Reads whether one report tab is the active one out of the combined report store. Indexes s.isActiveTab by the given key and falls back to IS_ACTIVE_TAB_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsActiveTab = (key: string) => (s: ReportState) =>
  s.isActiveTab[key] ?? IS_ACTIVE_TAB_DEFAULT;
/**
 * Reads whether one report tab has no data to show out of the combined report store. Indexes s.isEmptyData by the given key and falls back to IS_EMPTY_DATA_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsEmptyData = (key: string) => (s: ReportState) =>
  s.isEmptyData[key] ?? IS_EMPTY_DATA_DEFAULT;
/**
 * Reads the heart-rate chart reload counter of one report tab out of the combined report store. Indexes s.reloadHrChart by the given key and falls back to RELOAD_HR_CHART_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectReloadHrChart = (key: string) => (s: ReportState) =>
  s.reloadHrChart[key] ?? RELOAD_HR_CHART_DEFAULT;
/**
 * Reads whether the heart-rate chart of one report tab is loading out of the combined report store. Indexes s.isLoadingHrChart by the given key and falls back to IS_LOADING_HR_CHART_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsLoadingHrChart = (key: string) => (s: ReportState) =>
  s.isLoadingHrChart[key] ?? IS_LOADING_HR_CHART_DEFAULT;
/**
 * Reads whether the heart-rate chart options of one report tab are loading out of the combined report store. Indexes s.isLoadingHrChartOptions by the given key and falls back to IS_LOADING_HR_CHART_OPTIONS_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsLoadingHrChartOptions = (key: string) => (s: ReportState) =>
  s.isLoadingHrChartOptions[key] ?? IS_LOADING_HR_CHART_OPTIONS_DEFAULT;

/** Same literal as v1's `defaultEventFilter` (`Recoil/index.ts`). */
const EVENT_FILTER_DEFAULT: IEventFilter = {
  isCapture: false,
  isHideReviewed: false,
  isAvgHrBelow100: false,
  sortOrder: undefined,
  sortBy: undefined,
};
const SPECIAL_EVENT_COUNT_DEFAULT = Object.freeze({}) as IRhythmEventCount | IBeatEventCount;
const IS_SHOW_ARTIFACT_EVENT_DEFAULT = true;
const IS_SHOW_AFIB_EVENT_DEFAULT = true;
const IS_SHOW_OTHER_EVENT_DEFAULT = true;
/** Same literal as v1's `defaultControlHrSummaryModalValue` (`RecoilEffect/recoilHrSummary/index.ts`). */
export const DEFAULT_CONTROL_HR_SUMMARY_MODAL_VALUE: IControlModalHrSummary = {
  isShowModalSetMaxMinHr: false,
  isShowModalEditHr: false,
};

/**
 * Reads the event filter of one report tab out of the combined report store. Indexes s.eventFilter by the given key and falls back to EVENT_FILTER_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectEventFilter = (key: string) => (s: ReportState) =>
  s.eventFilter[key] ?? EVENT_FILTER_DEFAULT;
/**
 * Reads the special event counts of one report tab out of the combined report store. Indexes s.specialEventCount by the given key and falls back to SPECIAL_EVENT_COUNT_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSpecialEventCount = (key: string) => (s: ReportState) =>
  s.specialEventCount[key] ?? SPECIAL_EVENT_COUNT_DEFAULT;
/**
 * Reads whether artifact events are shown on one report tab out of the combined report store. Indexes s.isShowArtifactEvent by the given key and falls back to IS_SHOW_ARTIFACT_EVENT_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsShowArtifactEvent = (key: string) => (s: ReportState) =>
  s.isShowArtifactEvent[key] ?? IS_SHOW_ARTIFACT_EVENT_DEFAULT;
/**
 * Reads whether AFib events are shown on one report tab out of the combined report store. Indexes s.isShowAfibEvent by the given key and falls back to IS_SHOW_AFIB_EVENT_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsShowAfibEvent = (key: string) => (s: ReportState) =>
  s.isShowAfibEvent[key] ?? IS_SHOW_AFIB_EVENT_DEFAULT;
/**
 * Reads whether other events are shown on one report tab out of the combined report store. Indexes s.isShowOtherEvent by the given key and falls back to IS_SHOW_OTHER_EVENT_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsShowOtherEvent = (key: string) => (s: ReportState) =>
  s.isShowOtherEvent[key] ?? IS_SHOW_OTHER_EVENT_DEFAULT;

const SELECTED_STRIP_DEFAULT: IEvent | IBeat | IEcgBookmark | null = null;
const SELECTED_MULTIPLE_STRIP_DEFAULT = Object.freeze([]) as unknown as Array<IEvent | IBeat>;
const IS_LOADING_STRIP_DEFAULT = true;
const SELECTED_COLUMN_INFO_DEFAULT = Object.freeze([]) as unknown as Array<IEvent | IPoint>;
const PREV_COLUMN_INFO_DEFAULT = Object.freeze([]) as unknown as Array<IEvent | IPoint>;
const NEXT_COLUMN_INFO_DEFAULT = Object.freeze([]) as unknown as Array<IEvent | IPoint>;
const SELECTED_EVENT_INDEX_DEFAULT = 0;
const SELECTED_EVENT_DEFAULT = Object.freeze([]) as unknown as Array<IEvent | IPoint>;
const SELECTED_EVENT_INFO_DEFAULT = Object.freeze([]) as unknown as Array<IEvent | IPoint>;
const SELECTED_EVENT_INFO_INDEX_DEFAULT = 0;
const HOLTER_EVENTS_DEFAULT = Object.freeze([]) as unknown as Array<IEvent>;
const COMBINED_EVENTS_DEFAULT = Object.freeze([]) as unknown as Array<IEvent>;
const MINUTE_INFO_EVENTS_DEFAULT = Object.freeze([]) as unknown as Array<IEventOther>;
const IS_OPEN_MODAL_EDIT_STRIP_DEFAULT = false;
/** v1's `defaultControlModalValue`, kept under this name for existing consumers. */
export const DEFAULT_CONTROL_MODAL_VALUE = defaultControlModalValue;

/**
 * Reads the strip selected on one report tab out of the combined report store. Indexes s.selectedStrip by the given key and falls back to SELECTED_STRIP_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSelectedStrip = (key: string) => (s: ReportState) =>
  s.selectedStrip[key] ?? SELECTED_STRIP_DEFAULT;
/**
 * Reads the strips multi-selected on one report tab out of the combined report store. Indexes s.selectedMultipleStrip by the given key and falls back to SELECTED_MULTIPLE_STRIP_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSelectedMultipleStrip = (key: string) => (s: ReportState) =>
  s.selectedMultipleStrip[key] ?? SELECTED_MULTIPLE_STRIP_DEFAULT;
/**
 * Reads whether the strips of one report tab are loading out of the combined report store. Indexes s.isLoadingStrip by the given key and falls back to IS_LOADING_STRIP_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsLoadingStrip = (key: string) => (s: ReportState) =>
  s.isLoadingStrip[key] ?? IS_LOADING_STRIP_DEFAULT;
/**
 * Reads the selected column info of one report tab out of the combined report store. Indexes s.selectedColumnInfo by the given key and falls back to SELECTED_COLUMN_INFO_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSelectedColumnInfo = (key: string) => (s: ReportState) =>
  s.selectedColumnInfo[key] ?? SELECTED_COLUMN_INFO_DEFAULT;
/**
 * Reads the previous-column info of one report tab out of the combined report store. Indexes s.prevColumnInfo by the given key and falls back to PREV_COLUMN_INFO_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectPrevColumnInfo = (key: string) => (s: ReportState) =>
  s.prevColumnInfo[key] ?? PREV_COLUMN_INFO_DEFAULT;
/**
 * Reads the next-column info of one report tab out of the combined report store. Indexes s.nextColumnInfo by the given key and falls back to NEXT_COLUMN_INFO_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectNextColumnInfo = (key: string) => (s: ReportState) =>
  s.nextColumnInfo[key] ?? NEXT_COLUMN_INFO_DEFAULT;
/**
 * Reads the index of the event selected on one report tab out of the combined report store. Indexes s.selectedEventIndex by the given key and falls back to SELECTED_EVENT_INDEX_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSelectedEventIndex = (key: string) => (s: ReportState) =>
  s.selectedEventIndex[key] ?? SELECTED_EVENT_INDEX_DEFAULT;
/**
 * Reads the events selected on one report tab out of the combined report store. Indexes s.selectedEvent by the given key and falls back to SELECTED_EVENT_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSelectedEvent = (key: string) => (s: ReportState) =>
  s.selectedEvent[key] ?? SELECTED_EVENT_DEFAULT;
/**
 * Reads the info of the events selected on one report tab out of the combined report store. Indexes s.selectedEventInfo by the given key and falls back to SELECTED_EVENT_INFO_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSelectedEventInfo = (key: string) => (s: ReportState) =>
  s.selectedEventInfo[key] ?? SELECTED_EVENT_INFO_DEFAULT;
/**
 * Reads the index into the selected event info of one report tab out of the combined report store. Indexes s.selectedEventInfoIndex by the given key and falls back to SELECTED_EVENT_INFO_INDEX_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectSelectedEventInfoIndex = (key: string) => (s: ReportState) =>
  s.selectedEventInfoIndex[key] ?? SELECTED_EVENT_INFO_INDEX_DEFAULT;
/**
 * Reads the Holter events of one report tab out of the combined report store. Indexes s.holterEvents by the given key and falls back to HOLTER_EVENTS_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectHolterEvents = (key: string) => (s: ReportState) =>
  s.holterEvents[key] ?? HOLTER_EVENTS_DEFAULT;
/**
 * Reads the merged event list of one report tab out of the combined report store. Indexes s.combinedEvents by the given key and falls back to COMBINED_EVENTS_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectCombinedEvents = (key: string) => (s: ReportState) =>
  s.combinedEvents[key] ?? COMBINED_EVENTS_DEFAULT;
/**
 * Reads the per-minute info events of one report tab out of the combined report store. Indexes s.minuteInfoEvents by the given key and falls back to MINUTE_INFO_EVENTS_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectMinuteInfoEvents = (key: string) => (s: ReportState) =>
  s.minuteInfoEvents[key] ?? MINUTE_INFO_EVENTS_DEFAULT;
/**
 * Reads whether the edit-strip modal of one report tab is open out of the combined report store. Indexes s.isOpenModalEditStrip by the given key and falls back to IS_OPEN_MODAL_EDIT_STRIP_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectIsOpenModalEditStrip = (key: string) => (s: ReportState) =>
  s.isOpenModalEditStrip[key] ?? IS_OPEN_MODAL_EDIT_STRIP_DEFAULT;
/**
 * Reads the control modal state of one report tab out of the combined report store. Indexes s.controlModal by the given key and falls back to DEFAULT_CONTROL_MODAL_VALUE when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectControlModal = (key: string) => (s: ReportState) =>
  s.controlModal[key] ?? DEFAULT_CONTROL_MODAL_VALUE;

const EVENT_CHANGES_DEFAULT = Object.freeze([]) as unknown as Array<IEvent>;
const EVENT_NEWS_DEFAULT = Object.freeze([]) as unknown as Array<IEvent>;
const EVENT_OTHERS_DEFAULT = Object.freeze([]) as unknown as Array<IEventOther>;
const BEAT_CHANGES_DEFAULT = Object.freeze([]) as unknown as Array<IBeatChange>;
const RHYTHM_EVENT_COUNT_DEFAULT: IRhythmEventCount = {
  afib: { count: 0, reviewed: 0 },
  tachy: { count: 0, reviewed: 0 },
  brady: { count: 0, reviewed: 0 },
  pause: { count: 0, reviewed: 0 },
  maxHr: { count: 0, reviewed: 0 },
  minHr: { count: 0, reviewed: 0 },
  vt: { count: 0, reviewed: 0 },
  svt: { count: 0, reviewed: 0 },
  avb2: { count: 0, reviewed: 0 },
  avb3: { count: 0, reviewed: 0 },
};
const BEAT_EVENT_COUNT_DEFAULT: IBeatEventCount = {
  veCouplet: { count: 0, reviewed: 0 },
  veBigeminy: { count: 0, reviewed: 0 },
  veTrigeminal: { count: 0, reviewed: 0 },
  veRun: { count: 0, reviewed: 0 },
  veQuadrigeminy: { count: 0, reviewed: 0 },
  sveCouplet: { count: 0, reviewed: 0 },
  sveBigeminy: { count: 0, reviewed: 0 },
  sveTrigeminal: { count: 0, reviewed: 0 },
  sveRun: { count: 0, reviewed: 0 },
  sveQuadrigeminy: { count: 0, reviewed: 0 },
};

/**
 * Reads the pending event edits of one report tab out of the combined report store. Indexes s.eventChanges by the given key and falls back to EVENT_CHANGES_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectEventChanges = (key: string) => (s: ReportState) =>
  s.eventChanges[key] ?? EVENT_CHANGES_DEFAULT;
/**
 * Zustand replacement for the orphaned Recoil selector `updatedSelectedStripState`
 * (`Recoil/index.ts`), which reads `eventChangesState`/`selectedStripState` — both
 * long migrated here — and so had gone permanently stale.
 */
/**
 * Reads the strip selected on one report tab, preferring its edited copy over the stored one. Resolves the tab's selected strip, then scans the tab's pending event changes for an entry with the same id and returns that instead when one exists.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns the selected strip with any pending edit applied.
 */
export const selectUpdatedSelectedStrip = (key: string) => (s: ReportState) => {
  const selectedStrip = selectSelectedStrip(key)(s);
  return selectEventChanges(key)(s).find((x) => x.id === selectedStrip?.id) ?? selectedStrip;
};
/**
 * Reads the newly arrived events of one report tab out of the combined report store. Indexes s.eventNews by the given key and falls back to EVENT_NEWS_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectEventNews = (key: string) => (s: ReportState) =>
  s.eventNews[key] ?? EVENT_NEWS_DEFAULT;
/**
 * Reads the other-event list of one report tab out of the combined report store. Indexes s.eventOthers by the given key and falls back to EVENT_OTHERS_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectEventOthers = (key: string) => (s: ReportState) =>
  s.eventOthers[key] ?? EVENT_OTHERS_DEFAULT;
/**
 * Reads the pending beat edits of one report tab out of the combined report store. Indexes s.beatChanges by the given key and falls back to BEAT_CHANGES_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectBeatChanges = (key: string) => (s: ReportState) =>
  s.beatChanges[key] ?? BEAT_CHANGES_DEFAULT;

const GROUP_EVENT_CHANGES_DEFAULT = Object.freeze([]) as unknown as Array<IGroupEventChanges>;
// v1's own literal (`Recoil/index.ts`) keys this by the old atom names, not field names.
const DEFAULT_EVENT_OPTIONS = defaultEventOptions;
const DEFAULT_BEAT_OPTIONS = {
  beatChangesState: [] as any[],
  resetBulkBeatsChangesState: [] as any[],
  bulkBeatsChangesState: [] as any[],
  swapBeatChangesState: [] as any[],
  bulkSwapBeatsState: [] as any[],
  resetSwapBeatsState: [] as any[],
  bulkReviewedBeatsState: [] as any[],
  resetBulkReviewedBeatsState: [] as any[],
  reviewedBeatsChangesState: [] as any[],
};
const BULK_BEATS_CHANGES_DEFAULT: any = Object.freeze([]);
const RESET_BULK_BEATS_CHANGES_DEFAULT: any = Object.freeze([]);

/**
 * Reads the grouped event edits of one report tab out of the combined report store. Indexes s.groupEventChanges by the given key and falls back to GROUP_EVENT_CHANGES_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectGroupEventChanges = (key: string) => (s: ReportState) =>
  s.groupEventChanges[key] ?? GROUP_EVENT_CHANGES_DEFAULT;
/**
 * Reads the event display options of one report tab out of the combined report store. Indexes s.eventOptions by the given key and falls back to DEFAULT_EVENT_OPTIONS when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectEventOptions = (key: string) => (s: ReportState) =>
  s.eventOptions[key] ?? DEFAULT_EVENT_OPTIONS;
/**
 * Reads the beat display options of one report tab out of the combined report store. Indexes s.beatOptions by the given key and falls back to DEFAULT_BEAT_OPTIONS when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectBeatOptions = (key: string) => (s: ReportState) =>
  s.beatOptions[key] ?? DEFAULT_BEAT_OPTIONS;
/**
 * Reads the bulk beat changes queued on one report tab out of the combined report store. Indexes s.bulkBeatsChanges by the given key and falls back to BULK_BEATS_CHANGES_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectBulkBeatsChanges = (key: string) => (s: ReportState) =>
  s.bulkBeatsChanges[key] ?? BULK_BEATS_CHANGES_DEFAULT;
/**
 * Reads the reset signal for bulk beat changes on one report tab out of the combined report store. Indexes s.resetBulkBeatsChanges by the given key and falls back to RESET_BULK_BEATS_CHANGES_DEFAULT when that tab has no entry.
 *
 * @param key - The report-tab store key whose entry is read.
 * @returns A selector that takes the report state and returns that tab's value.
 */
export const selectResetBulkBeatsChanges = (key: string) => (s: ReportState) =>
  s.resetBulkBeatsChanges[key] ?? RESET_BULK_BEATS_CHANGES_DEFAULT;

export interface ReportState {
  reportData: any;
  findings: String;
  findingsChange: String;
  facilityNote: String;
  progressDownload: number;
  estimateDownload: number | undefined;
  isDownloadingPdfReport: boolean;
  viewHrMode: Record<string, ViewHrMode>;
  selectedHrTime: Record<string, string | undefined>;
  originalDailySummaries: Array<IDailySummary>;
  minHrTabActive: boolean;
  hrDataDefault: IHrSummary;
  dailySummariesCombine: Array<IDailySummary>;
  maxHrsInDate: Array<IHrsInDate>;
  minHrsInDate: Array<IHrsInDate>;
  ecgBookmarkDeleteIds: Array<string>;
  isViewNewEvent: Record<string, boolean>;
  reloadViewSizeConfig: number;
  updatedAfibAvgHr: number | undefined;
  jumpDateStatus: Record<string, TJumpDateStatus>;
  chartHrsData: Record<string, Array<IHrChartData>>;
  sizeStripManagementThumbnailConfig: ISizeThumbnailConfig;
  sizeStripDeviceThumbnailConfig: ISizeThumbnailConfig;
  selectedEcgDisclosure: IEcgDisclosure | undefined;
  ecgDisclosureChange: IEcgDisclosure | undefined;
  isOpenInvalidTimeModal: boolean;
  stripTotalStrip: number;
  totalStripDevice: number;
  eventStripsData: Record<string, Array<any>>;
  deviceEventData: Record<string, IDeviceEventData>;
  changeStripData: Record<string, Array<any>>;
  stripFilter: Record<string, IStripFilter>;
  deviceStripFilter: Record<string, ISdeviceStripFilter>;
  emptySelectedKey: Record<string, any>;
  reloadStripManagement: Record<string, number>;
  deviceEventCount: Record<string, IDeviceEventCount>;
  isSavingStripmanagement: boolean;
  isWaiting: Record<string, boolean>;
  isSkipSelectThumbnail: Record<string, boolean>;
  dailyArtifactCount: Record<string, Array<IDailyCount>>;
  sizeBeatThumbnailConfig: ISizeThumbnailConfig;
  morphologyTemplate: Record<string, Array<any>>;
  templateChanges: Record<string, Array<any>>;
  selectedTemplate: Record<string, any>;
  swapBeatChanges: Record<string, Array<any>>;
  originalTemplateOverview: any;
  initTemplateOverview: any;
  totalStrip: number;
  listSelectedCells: Array<IListSelectedCells>;
  loadingBeatStatus: ILoadBeatStatus;
  isHideReviewedCell: boolean;
  dataHeatmapChart: TDataHeatmapChart;
  dataHeatmapChartStatus: DataHeatmapChart<TDataHeatmapStatus>;
  rrOverviewOrigin: DataHeatmapChart<TRrOverview>;
  combinedEventsInHeatmap: any;
  bulkAction: Record<string, TBulkAction>;
  bulkActionSwap: Record<string, any>;
  bulkSwapBeats: Record<string, any>;
  resetSwapBeats: Record<string, Array<any>>;
  bulkActionByCell: Record<string, TBulkAction>;
  viewBeatsSource: (typeof TEMPLATE_SOURCE_OPTIONS)[number];
  bulkReviewedBeats: any;
  reviewedBeatsChanges: any;
  resetBulkReviewedBeats: any;
  visibleReviewedFilter: string;
  beatsSortType: string;
  isLoadedIndexDB: boolean;
  reportInfo: IReportInfo | {};
  isStartedAiProcessing: boolean;
  isOpenProcessingLimit: Boolean;
  limitProcessingStudy: any;
  isShowTabHeader: boolean;
  isPushQueue: boolean;
  isCompletedDownloadBeat: boolean;
  isDownloadingBeatFile: boolean;
  listDownloadFile: any;
  profileAfibAvgHr: any;
  isDisabledBtn: boolean;
  isUndoDisabled: boolean;
  isRedoDisabled: boolean;
  dateList: Array<any>;
  specialEventTypes: ISpecialEventTypes;
  actionSnapshot: any;
  isRenderViewerEcg: boolean;
  sizeEventThumbnailConfig: ISizeThumbnailConfig;
  highPassThumbnail: number;
  gainThumbnail: number;
  channelsThumbnail: Array<number>;
  aiChannel: Record<string, TAiChannel | null>;
  ecgObject: Record<string, any>;
  reloadEcgViewer: Record<string, number>;
  isLoadingEcgViewer: Record<string, boolean>;
  preventReloadEcg: Record<string, boolean>;
  ecgDataMap: any;
  pickDateAction: Record<string, TPickDateAction | null>;
  pickedStartDate: Record<string, string | null>;
  pickedEndDate: Record<string, string | null>;
  pickedBookmarkDate: Record<string, string | null>;
  jumpDate: Record<string, any>;
  jumpTime: Record<string, number | undefined>;
  jumpPortion: Record<string, number | undefined>;
  selectedDateValue: Record<string, string | null>;
  dailyCount: Record<string, Array<IDailyCount>>;
  activeTab: Record<string, TBeatHrActiveTab | TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM>;
  pageIndex: Record<string, IPageIndex>;
  isActiveTab: Record<string, boolean>;
  isEmptyData: Record<string, boolean>;
  reloadHrChart: Record<string, number>;
  isLoadingHrChart: Record<string, boolean>;
  isLoadingHrChartOptions: Record<string, boolean>;
  eventFilter: Record<string, IEventFilter>;
  specialEventCount: Record<string, IRhythmEventCount | IBeatEventCount>;
  isShowArtifactEvent: Record<string, boolean>;
  isShowAfibEvent: Record<string, boolean>;
  isShowOtherEvent: Record<string, boolean>;
  controlHrSummaryModal: IControlModalHrSummary;
  selectedStrip: Record<string, IEvent | IBeat | IEcgBookmark | null>;
  selectedMultipleStrip: Record<string, Array<IEvent | IBeat>>;
  isLoadingStrip: Record<string, boolean>;
  selectedColumnInfo: Record<string, Array<IEvent | IPoint>>;
  prevColumnInfo: Record<string, Array<IEvent | IPoint>>;
  nextColumnInfo: Record<string, Array<IEvent | IPoint>>;
  selectedEventIndex: Record<string, number>;
  selectedEvent: Record<string, Array<IEvent | IPoint>>;
  selectedEventInfo: Record<string, Array<IEvent | IPoint>>;
  selectedEventInfoIndex: Record<string, number>;
  holterEvents: Record<string, Array<IEvent>>;
  combinedEvents: Record<string, Array<IEvent>>;
  minuteInfoEvents: Record<string, Array<IEventOther>>;
  isOpenModalEditStrip: Record<string, boolean>;
  controlModal: Record<string, IControlModal>;
  eventChanges: Record<string, Array<IEvent>>;
  eventNews: Record<string, Array<IEvent>>;
  eventOthers: Record<string, Array<IEventOther>>;
  beatChanges: Record<string, Array<IBeatChange>>;
  rhythmEventCount: IRhythmEventCount;
  beatEventCount: IBeatEventCount;
  groupEventChanges: Record<string, Array<IGroupEventChanges>>;
  eventOptions: Record<string, any>;
  beatOptions: Record<string, any>;
  bulkBeatsChanges: Record<string, any>;
  resetBulkBeatsChanges: Record<string, any>;
  ecgBookmarks: Array<IEcgBookmark>;
  updatedDailySummaries: Array<IDailySummary>;
  actions: {
    setReportData: (v: Setter<any>) => void;
    setFindings: (v: Setter<String>) => void;
    setFindingsChange: (v: Setter<String>) => void;
    setFacilityNote: (v: Setter<String>) => void;
    setProgressDownload: (v: Setter<number>) => void;
    setEstimateDownload: (v: Setter<number | undefined>) => void;
    setIsDownloadingPdfReport: (v: Setter<boolean>) => void;
    setViewHrMode: (key: string, v: Setter<ViewHrMode>) => void;
    setSelectedHrTime: (key: string, v: Setter<string | undefined>) => void;
    setOriginalDailySummaries: (v: Setter<Array<IDailySummary>>) => void;
    setMinHrTabActive: (v: Setter<boolean>) => void;
    setHrDataDefault: (v: Setter<IHrSummary>) => void;
    setDailySummariesCombine: (v: Setter<Array<IDailySummary>>) => void;
    setMaxHrsInDate: (v: Setter<Array<IHrsInDate>>) => void;
    setMinHrsInDate: (v: Setter<Array<IHrsInDate>>) => void;
    setEcgBookmarkDeleteIds: (v: Setter<Array<string>>) => void;
    setIsViewNewEvent: (key: string, v: Setter<boolean>) => void;
    setReloadViewSizeConfig: (v: Setter<number>) => void;
    setUpdatedAfibAvgHr: (v: Setter<number | undefined>) => void;
    setJumpDateStatus: (key: string, v: Setter<TJumpDateStatus>) => void;
    setChartHrsData: (key: string, v: Setter<Array<IHrChartData>>) => void;
    setSizeStripManagementThumbnailConfig: (v: Setter<ISizeThumbnailConfig>) => void;
    setSizeStripDeviceThumbnailConfig: (v: Setter<ISizeThumbnailConfig>) => void;
    setSelectedEcgDisclosure: (v: Setter<IEcgDisclosure | undefined>) => void;
    setEcgDisclosureChange: (v: Setter<IEcgDisclosure | undefined>) => void;
    setIsOpenInvalidTimeModal: (v: Setter<boolean>) => void;
    setStripTotalStrip: (v: Setter<number>) => void;
    setTotalStripDevice: (v: Setter<number>) => void;
    setEventStripsData: (key: string, v: Setter<Array<any>>) => void;
    setDeviceEventData: (key: string, v: Setter<IDeviceEventData>) => void;
    setChangeStripData: (key: string, v: Setter<Array<any>>) => void;
    setStripFilter: (key: string, v: Setter<IStripFilter>) => void;
    setDeviceStripFilter: (key: string, v: Setter<ISdeviceStripFilter>) => void;
    setEmptySelectedKey: (key: string, v: Setter<any>) => void;
    setReloadStripManagement: (key: string, v: Setter<number>) => void;
    setDeviceEventCount: (key: string, v: Setter<IDeviceEventCount>) => void;
    setIsSavingStripmanagement: (v: Setter<boolean>) => void;
    setIsWaiting: (key: string, v: Setter<boolean>) => void;
    setIsSkipSelectThumbnail: (key: string, v: Setter<boolean>) => void;
    setDailyArtifactCount: (key: string, v: Setter<Array<IDailyCount>>) => void;
    setSizeBeatThumbnailConfig: (v: Setter<ISizeThumbnailConfig>) => void;
    setMorphologyTemplate: (key: string, v: Setter<Array<any>>) => void;
    setTemplateChanges: (key: string, v: Setter<Array<any>>) => void;
    setSelectedTemplate: (key: string, v: Setter<any>) => void;
    setSwapBeatChanges: (key: string, v: Setter<Array<any>>) => void;
    setOriginalTemplateOverview: (v: Setter<any>) => void;
    setInitTemplateOverview: (v: Setter<any>) => void;
    setTotalStrip: (v: Setter<number>) => void;
    setListSelectedCells: (v: Setter<Array<IListSelectedCells>>) => void;
    setLoadingBeatStatus: (v: Setter<ILoadBeatStatus>) => void;
    setIsHideReviewedCell: (v: Setter<boolean>) => void;
    setDataHeatmapChart: (v: Setter<TDataHeatmapChart>) => void;
    setDataHeatmapChartStatus: (v: Setter<DataHeatmapChart<TDataHeatmapStatus>>) => void;
    setRrOverviewOrigin: (v: Setter<DataHeatmapChart<TRrOverview>>) => void;
    setCombinedEventsInHeatmap: (v: Setter<any>) => void;
    setBulkAction: (key: string, v: Setter<TBulkAction>) => void;
    setBulkActionSwap: (key: string, v: Setter<any>) => void;
    setBulkSwapBeats: (key: string, v: Setter<any>) => void;
    setResetSwapBeats: (key: string, v: Setter<Array<any>>) => void;
    setBulkActionByCell: (key: string, v: Setter<TBulkAction>) => void;
    setViewBeatsSource: (v: Setter<(typeof TEMPLATE_SOURCE_OPTIONS)[number]>) => void;
    setBulkReviewedBeats: (v: Setter<any>) => void;
    setReviewedBeatsChanges: (v: Setter<any>) => void;
    setResetBulkReviewedBeats: (v: Setter<any>) => void;
    setVisibleReviewedFilter: (v: Setter<string>) => void;
    setBeatsSortType: (v: Setter<string>) => void;
    setIsLoadedIndexDB: (v: Setter<boolean>) => void;
    setReportInfo: (v: Setter<IReportInfo | {}>) => void;
    setIsStartedAiProcessing: (v: Setter<boolean>) => void;
    setIsOpenProcessingLimit: (v: Setter<Boolean>) => void;
    setLimitProcessingStudy: (v: Setter<any>) => void;
    setIsShowTabHeader: (v: Setter<boolean>) => void;
    setIsPushQueue: (v: Setter<boolean>) => void;
    setIsCompletedDownloadBeat: (v: Setter<boolean>) => void;
    setIsDownloadingBeatFile: (v: Setter<boolean>) => void;
    setListDownloadFile: (v: Setter<any>) => void;
    setProfileAfibAvgHr: (v: Setter<any>) => void;
    setIsDisabledBtn: (v: Setter<boolean>) => void;
    setIsUndoDisabled: (v: Setter<boolean>) => void;
    setIsRedoDisabled: (v: Setter<boolean>) => void;
    setDateList: (v: Setter<Array<any>>) => void;
    setSpecialEventTypes: (v: Setter<ISpecialEventTypes>) => void;
    setActionSnapshot: (v: Setter<any>) => void;
    setIsRenderViewerEcg: (v: Setter<boolean>) => void;
    setSizeEventThumbnailConfig: (v: Setter<ISizeThumbnailConfig>) => void;
    setHighPassThumbnail: (v: Setter<number>) => void;
    setGainThumbnail: (v: Setter<number>) => void;
    setChannelsThumbnail: (v: Setter<Array<number>>) => void;
    setAiChannel: (key: string, v: Setter<TAiChannel | null>) => void;
    setEcgObject: (key: string, v: Setter<any>) => void;
    setReloadEcgViewer: (key: string, v: Setter<number>) => void;
    setIsLoadingEcgViewer: (key: string, v: Setter<boolean>) => void;
    setPreventReloadEcg: (key: string, v: Setter<boolean>) => void;
    setEcgDataMap: (v: Setter<any>) => void;
    setPickDateAction: (key: string, v: Setter<TPickDateAction | null>) => void;
    setPickedStartDate: (key: string, v: Setter<string | null>) => void;
    setPickedEndDate: (key: string, v: Setter<string | null>) => void;
    setPickedBookmarkDate: (key: string, v: Setter<string | null>) => void;
    setJumpDate: (key: string, v: Setter<any>) => void;
    setJumpTime: (key: string, v: Setter<number | undefined>) => void;
    setJumpPortion: (key: string, v: Setter<number | undefined>) => void;
    setSelectedDateValue: (key: string, v: Setter<string | null>) => void;
    setDailyCount: (key: string, v: Setter<Array<IDailyCount>>) => void;
    setActiveTab: (
      key: string,
      v: Setter<TBeatHrActiveTab | TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM>,
    ) => void;
    setPageIndex: (key: string, v: Setter<IPageIndex>) => void;
    setIsActiveTab: (key: string, v: Setter<boolean>) => void;
    setIsEmptyData: (key: string, v: Setter<boolean>) => void;
    setReloadHrChart: (key: string, v: Setter<number>) => void;
    setIsLoadingHrChart: (key: string, v: Setter<boolean>) => void;
    setIsLoadingHrChartOptions: (key: string, v: Setter<boolean>) => void;
    setEventFilter: (key: string, v: Setter<IEventFilter>) => void;
    setSpecialEventCount: (key: string, v: Setter<IRhythmEventCount | IBeatEventCount>) => void;
    setIsShowArtifactEvent: (key: string, v: Setter<boolean>) => void;
    setIsShowAfibEvent: (key: string, v: Setter<boolean>) => void;
    setIsShowOtherEvent: (key: string, v: Setter<boolean>) => void;
    setControlHrSummaryModal: (v: Setter<IControlModalHrSummary>) => void;
    resetControlHrSummaryModal: () => void;
    setSelectedStrip: (key: string, v: Setter<IEvent | IBeat | IEcgBookmark | null>) => void;
    setSelectedMultipleStrip: (key: string, v: Setter<Array<IEvent | IBeat>>) => void;
    setIsLoadingStrip: (key: string, v: Setter<boolean>) => void;
    setSelectedColumnInfo: (key: string, v: Setter<Array<IEvent | IPoint>>) => void;
    setPrevColumnInfo: (key: string, v: Setter<Array<IEvent | IPoint>>) => void;
    setNextColumnInfo: (key: string, v: Setter<Array<IEvent | IPoint>>) => void;
    setSelectedEventIndex: (key: string, v: Setter<number>) => void;
    setSelectedEvent: (key: string, v: Setter<Array<IEvent | IPoint>>) => void;
    setSelectedEventInfo: (key: string, v: Setter<Array<IEvent | IPoint>>) => void;
    setSelectedEventInfoIndex: (key: string, v: Setter<number>) => void;
    setHolterEvents: (key: string, v: Setter<Array<IEvent>>) => void;
    setCombinedEvents: (key: string, v: Setter<Array<IEvent>>) => void;
    setMinuteInfoEvents: (key: string, v: Setter<Array<IEventOther>>) => void;
    setIsOpenModalEditStrip: (key: string, v: Setter<boolean>) => void;
    setControlModal: (key: string, v: Setter<IControlModal>) => void;
    resetControlModal: (key: string) => void;
    setEventChanges: (key: string, v: Setter<Array<IEvent>>) => void;
    setEventNews: (key: string, v: Setter<Array<IEvent>>) => void;
    setEventOthers: (key: string, v: Setter<Array<IEventOther>>) => void;
    setBeatChanges: (key: string, v: Setter<Array<IBeatChange>>) => void;
    setRhythmEventCount: (v: Setter<IRhythmEventCount>) => void;
    setBeatEventCount: (v: Setter<IBeatEventCount>) => void;
    setGroupEventChanges: (key: string, v: Setter<Array<IGroupEventChanges>>) => void;
    setEventOptions: (key: string, v: Setter<any>) => void;
    setBeatOptions: (key: string, v: Setter<any>) => void;
    setBulkBeatsChanges: (key: string, v: Setter<any>) => void;
    setResetBulkBeatsChanges: (key: string, v: Setter<any>) => void;
    setEcgBookmarks: (v: Setter<Array<IEcgBookmark>>) => void;
    setUpdatedDailySummaries: (v: Setter<Array<IDailySummary>>) => void;
  };
}

const persistFindings = createFieldPersistence('findingsState');
const persistFindingsChange = createFieldPersistence('findingsChangeState');
// Legacy IDB key is the short "facilityNote", NOT "facilityNoteState" (v1 handleEffect("facilityNote")).
const persistFacilityNote = createFieldPersistence('facilityNote');
const persistDailySummariesCombine = createFieldPersistence('dailySummariesCombineState');
const persistMaxHrsInDate = createFieldPersistence('maxHrsInDateState');
const persistMinHrsInDate = createFieldPersistence('minHrsInDateState');
const persistEcgBookmarkDeleteIds = createFieldPersistence('ecgBookmarkDeleteIdsState', {
  recordUndo: true,
});
const persistSizeStripManagementThumbnailConfig = createFieldPersistence(
  'sizeStripManagementThumbnailConfigState',
);
const persistSizeStripDeviceThumbnailConfig = createFieldPersistence(
  'sizeStripDeviceThumbnailConfigState',
);
const persistSelectedEcgDisclosure = createFieldPersistence('selectedEcgDisclosureState');
const persistEcgDisclosureChange = createFieldPersistence('ecgDisclosureChangeState', {
  recordUndo: true,
});
const persistSizeBeatThumbnailConfig = createFieldPersistence('sizeBeatThumbnailConfigState');
const persistIsHideReviewedCell = createFieldPersistence('isHideReviewedCellState');
const persistDataHeatmapChart = createFieldPersistence('dataHeatmapChartState');
const persistDataHeatmapChartStatus = createFieldPersistence('dataHeatmapChartStatusState');
const persistRrOverviewOrigin = createFieldPersistence('rrOverviewOriginState');
// v1's `effects` array for these 2 was STATIC (not a function of the tab key), so every tab
// key wrote to the SAME legacy record — reproduce with one fixed (non-keyed) persist fn.
const persistBulkAction = createFieldPersistence('bulkActionState');
const persistBulkActionSwap = createFieldPersistence('bulkActionSwapState');
const persistBulkReviewedBeats = createFieldPersistence('bulkReviewedBeatsState');
const persistReviewedBeatsChanges = createFieldPersistence('reviewedBeatsChangesState');
const persistResetBulkReviewedBeats = createFieldPersistence('resetBulkReviewedBeatsState');
const persistVisibleReviewedFilter = createFieldPersistence('visibleReviewedFilterState');
const persistBeatsSortType = createFieldPersistence('beatsSortTypeState');
const persistReportInfo = createFieldPersistence('reportInfoState');
const persistIsShowTabHeader = createFieldPersistence('isShowTabHeaderState');
const persistProfileAfibAvgHr = createFieldPersistence('profileAfibAvgHrState');
const persistIsRenderViewerEcg = createFieldPersistence('isRenderViewerEcgState');
const persistSizeEventThumbnailConfig = createFieldPersistence('sizeEventThumbnailConfigState');
const persistHighPassThumbnail = createFieldPersistence('highPassThumbnailState');
const persistGainThumbnail = createFieldPersistence('gainThumbnailState');
const persistChannelsThumbnail = createFieldPersistence('channelsThumbnailState');
const persistControlHrSummaryModal = createFieldPersistence('controlHrSummaryModalState');
const persistEcgBookmarks = createFieldPersistence('ecgBookmarksState', { recordUndo: true });
const persistUpdatedDailySummaries = createFieldPersistence('updatedDailySummariesState', {
  recordUndo: true,
});

export const useReportStore = create<ReportState>()((set) => ({
  reportData: {},
  findings: '' as unknown as String,
  findingsChange: '' as unknown as String,
  facilityNote: '' as unknown as String,
  progressDownload: 0,
  estimateDownload: undefined,
  isDownloadingPdfReport: true,
  viewHrMode: {},
  selectedHrTime: {},
  originalDailySummaries: [],
  minHrTabActive: true,
  hrDataDefault: HR_DATA_INITIAL,
  dailySummariesCombine: [],
  maxHrsInDate: [],
  minHrsInDate: [],
  ecgBookmarkDeleteIds: [],
  isViewNewEvent: {},
  reloadViewSizeConfig: 0,
  updatedAfibAvgHr: undefined,
  jumpDateStatus: {},
  chartHrsData: {},
  sizeStripManagementThumbnailConfig: SIZE_STRIP_MANAGEMENT_THUMBNAIL[SIZE_THUMBNAIL_OPTIONS.SMALL],
  sizeStripDeviceThumbnailConfig:
    SIZE_DEVICE_STRIP_MANAGEMENT_THUMBNAIL[SIZE_THUMBNAIL_OPTIONS.SMALL],
  selectedEcgDisclosure: undefined,
  ecgDisclosureChange: undefined,
  isOpenInvalidTimeModal: false,
  stripTotalStrip: 0,
  totalStripDevice: 0,
  eventStripsData: {},
  deviceEventData: {},
  changeStripData: {},
  stripFilter: {},
  deviceStripFilter: {},
  emptySelectedKey: {},
  reloadStripManagement: {},
  deviceEventCount: {},
  isSavingStripmanagement: false,
  isWaiting: {},
  isSkipSelectThumbnail: {},
  dailyArtifactCount: {},
  sizeBeatThumbnailConfig: SIZE_BEAT_THUMBNAIL[SIZE_THUMBNAIL_OPTIONS.MEDIUM],
  morphologyTemplate: {},
  templateChanges: {},
  selectedTemplate: {},
  swapBeatChanges: {},
  originalTemplateOverview: {},
  initTemplateOverview: [],
  totalStrip: 0,
  listSelectedCells: [],
  loadingBeatStatus: { status: LOAD_BEAT_STATUS.NONE },
  isHideReviewedCell: false,
  dataHeatmapChart: generateDefaultHeatmapState([]),
  dataHeatmapChartStatus: generateDefaultHeatmapState(DATA_HEATMAP_STATUS.INITIAL),
  rrOverviewOrigin: generateDefaultHeatmapState({ reviewed: 0, total: 1 }),
  combinedEventsInHeatmap: {},
  bulkAction: {},
  bulkActionSwap: {},
  bulkSwapBeats: {},
  resetSwapBeats: {},
  bulkActionByCell: {},
  viewBeatsSource: TEMPLATE_SOURCE_OPTIONS[0],
  bulkReviewedBeats: [],
  reviewedBeatsChanges: [],
  resetBulkReviewedBeats: [],
  visibleReviewedFilter: REVIEWED_BEAT_FILTER[0],
  beatsSortType: SORT_BEATS_OPTIONS[0],
  isLoadedIndexDB: false,
  reportInfo: REPORT_INFO_DEFAULT,
  isStartedAiProcessing: false,
  isOpenProcessingLimit: false,
  limitProcessingStudy: LIMIT_PROCESSING_STUDY_DEFAULT,
  isShowTabHeader: true,
  isPushQueue: true,
  isCompletedDownloadBeat: true,
  isDownloadingBeatFile: true,
  listDownloadFile: LIST_DOWNLOAD_FILE_DEFAULT,
  profileAfibAvgHr: undefined,
  isDisabledBtn: false,
  isUndoDisabled: true,
  isRedoDisabled: true,
  dateList: DATE_LIST_DEFAULT,
  specialEventTypes: SPECIAL_EVENT_TYPES_DEFAULT,
  actionSnapshot: 0,
  isRenderViewerEcg: true,
  sizeEventThumbnailConfig: SIZE_EVENT_THUMBNAIL[SIZE_THUMBNAIL_OPTIONS.SMALL],
  highPassThumbnail: 1,
  gainThumbnail: 7.5,
  channelsThumbnail: CHANNELS_THUMBNAIL_DEFAULT,
  aiChannel: {},
  ecgObject: {},
  reloadEcgViewer: {},
  isLoadingEcgViewer: {},
  preventReloadEcg: {},
  ecgDataMap: {},
  pickDateAction: {},
  pickedStartDate: {},
  pickedEndDate: {},
  pickedBookmarkDate: {},
  jumpDate: {},
  jumpTime: {},
  jumpPortion: {},
  selectedDateValue: {},
  dailyCount: {},
  activeTab: {},
  pageIndex: {},
  isActiveTab: {},
  isEmptyData: {},
  reloadHrChart: {},
  isLoadingHrChart: {},
  isLoadingHrChartOptions: {},
  eventFilter: {},
  specialEventCount: {},
  isShowArtifactEvent: {},
  isShowAfibEvent: {},
  isShowOtherEvent: {},
  controlHrSummaryModal: DEFAULT_CONTROL_HR_SUMMARY_MODAL_VALUE,
  selectedStrip: {},
  selectedMultipleStrip: {},
  isLoadingStrip: {},
  selectedColumnInfo: {},
  prevColumnInfo: {},
  nextColumnInfo: {},
  selectedEventIndex: {},
  selectedEvent: {},
  selectedEventInfo: {},
  selectedEventInfoIndex: {},
  holterEvents: {},
  combinedEvents: {},
  minuteInfoEvents: {},
  isOpenModalEditStrip: {},
  controlModal: {},
  eventChanges: {},
  eventNews: {},
  eventOthers: {},
  beatChanges: {},
  rhythmEventCount: RHYTHM_EVENT_COUNT_DEFAULT,
  beatEventCount: BEAT_EVENT_COUNT_DEFAULT,
  groupEventChanges: {},
  eventOptions: {},
  beatOptions: {},
  bulkBeatsChanges: {},
  resetBulkBeatsChanges: {},
  ecgBookmarks: [],
  updatedDailySummaries: [],
  actions: {
    setReportData: (v) => set((s) => ({ reportData: resolve(v, s.reportData) })),
    setProgressDownload: (v) => set((s) => ({ progressDownload: resolve(v, s.progressDownload) })),
    setEstimateDownload: (v) => set((s) => ({ estimateDownload: resolve(v, s.estimateDownload) })),
    setIsDownloadingPdfReport: (v) =>
      set((s) => ({ isDownloadingPdfReport: resolve(v, s.isDownloadingPdfReport) })),
    setViewHrMode: (key, v) =>
      set((s) => {
        const prev = selectViewHrMode(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('viewHrModeState', key)(next, prev);
        return { viewHrMode: { ...s.viewHrMode, [key]: next } };
      }),
    setSelectedHrTime: (key, v) =>
      set((s) => ({
        selectedHrTime: { ...s.selectedHrTime, [key]: resolve(v, s.selectedHrTime[key]) },
      })),
    setOriginalDailySummaries: (v) =>
      set((s) => ({ originalDailySummaries: resolve(v, s.originalDailySummaries) })),
    setMinHrTabActive: (v) => set((s) => ({ minHrTabActive: resolve(v, s.minHrTabActive) })),
    setHrDataDefault: (v) => set((s) => ({ hrDataDefault: resolve(v, s.hrDataDefault) })),
    setDailySummariesCombine: (v) =>
      set((s) => {
        const next = resolve(v, s.dailySummariesCombine);
        persistDailySummariesCombine(next, s.dailySummariesCombine);
        return { dailySummariesCombine: next };
      }),
    setMaxHrsInDate: (v) =>
      set((s) => {
        const next = resolve(v, s.maxHrsInDate);
        persistMaxHrsInDate(next, s.maxHrsInDate);
        return { maxHrsInDate: next };
      }),
    setMinHrsInDate: (v) =>
      set((s) => {
        const next = resolve(v, s.minHrsInDate);
        persistMinHrsInDate(next, s.minHrsInDate);
        return { minHrsInDate: next };
      }),
    setFindings: (v) =>
      set((s) => {
        const next = resolve(v, s.findings);
        persistFindings(next, s.findings);
        return { findings: next };
      }),
    setFindingsChange: (v) =>
      set((s) => {
        const next = resolve(v, s.findingsChange);
        persistFindingsChange(next, s.findingsChange);
        return { findingsChange: next };
      }),
    setFacilityNote: (v) =>
      set((s) => {
        const next = resolve(v, s.facilityNote);
        persistFacilityNote(next, s.facilityNote);
        return { facilityNote: next };
      }),
    setEcgBookmarkDeleteIds: (v) =>
      set((s) => {
        const next = resolve(v, s.ecgBookmarkDeleteIds);
        persistEcgBookmarkDeleteIds(next, s.ecgBookmarkDeleteIds);
        return { ecgBookmarkDeleteIds: next };
      }),
    setIsViewNewEvent: (key, v) =>
      set((s) => ({
        isViewNewEvent: { ...s.isViewNewEvent, [key]: resolve(v, selectIsViewNewEvent(key)(s)) },
      })),
    setReloadViewSizeConfig: (v) =>
      set((s) => ({ reloadViewSizeConfig: resolve(v, s.reloadViewSizeConfig) })),
    setUpdatedAfibAvgHr: (v) => set((s) => ({ updatedAfibAvgHr: resolve(v, s.updatedAfibAvgHr) })),
    setJumpDateStatus: (key, v) =>
      set((s) => {
        const prev = selectJumpDateStatus(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('jumpDateStatusState', key)(next, prev);
        return { jumpDateStatus: { ...s.jumpDateStatus, [key]: next } };
      }),
    setChartHrsData: (key, v) =>
      set((s) => ({
        chartHrsData: { ...s.chartHrsData, [key]: resolve(v, selectChartHrsData(key)(s)) },
      })),
    setSizeStripManagementThumbnailConfig: (v) =>
      set((s) => {
        const next = resolve(v, s.sizeStripManagementThumbnailConfig);
        persistSizeStripManagementThumbnailConfig(next, s.sizeStripManagementThumbnailConfig);
        return { sizeStripManagementThumbnailConfig: next };
      }),
    setSizeStripDeviceThumbnailConfig: (v) =>
      set((s) => {
        const next = resolve(v, s.sizeStripDeviceThumbnailConfig);
        persistSizeStripDeviceThumbnailConfig(next, s.sizeStripDeviceThumbnailConfig);
        return { sizeStripDeviceThumbnailConfig: next };
      }),
    setSelectedEcgDisclosure: (v) =>
      set((s) => {
        const next = resolve(v, s.selectedEcgDisclosure);
        persistSelectedEcgDisclosure(next, s.selectedEcgDisclosure);
        return { selectedEcgDisclosure: next };
      }),
    setEcgDisclosureChange: (v) =>
      set((s) => {
        const next = resolve(v, s.ecgDisclosureChange);
        persistEcgDisclosureChange(next, s.ecgDisclosureChange);
        return { ecgDisclosureChange: next };
      }),
    setIsOpenInvalidTimeModal: (v) =>
      set((s) => ({ isOpenInvalidTimeModal: resolve(v, s.isOpenInvalidTimeModal) })),
    setStripTotalStrip: (v) => set((s) => ({ stripTotalStrip: resolve(v, s.stripTotalStrip) })),
    setTotalStripDevice: (v) => set((s) => ({ totalStripDevice: resolve(v, s.totalStripDevice) })),
    setEventStripsData: (key, v) =>
      set((s) => ({
        eventStripsData: { ...s.eventStripsData, [key]: resolve(v, selectEventStripsData(key)(s)) },
      })),
    setDeviceEventData: (key, v) =>
      set((s) => {
        const prev = selectDeviceEventData(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('deviceEventDataState', key)(next, prev);
        return { deviceEventData: { ...s.deviceEventData, [key]: next } };
      }),
    setChangeStripData: (key, v) =>
      set((s) => {
        const prev = selectChangeStripData(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('changeStripDataState', key, { recordUndo: true })(next, prev);
        return { changeStripData: { ...s.changeStripData, [key]: next } };
      }),
    setStripFilter: (key, v) =>
      set((s) => {
        const prev = selectStripFilter(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('stripFilterState', key)(next, prev);
        return { stripFilter: { ...s.stripFilter, [key]: next } };
      }),
    setDeviceStripFilter: (key, v) =>
      set((s) => {
        const prev = selectDeviceStripFilter(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('deviceStripFilterState', key)(next, prev);
        return { deviceStripFilter: { ...s.deviceStripFilter, [key]: next } };
      }),
    setEmptySelectedKey: (key, v) =>
      set((s) => ({
        emptySelectedKey: {
          ...s.emptySelectedKey,
          [key]: resolve(v, selectEmptySelectedKey(key)(s)),
        },
      })),
    setReloadStripManagement: (key, v) =>
      set((s) => ({
        reloadStripManagement: {
          ...s.reloadStripManagement,
          [key]: resolve(v, selectReloadStripManagement(key)(s)),
        },
      })),
    setDeviceEventCount: (key, v) =>
      set((s) => ({
        deviceEventCount: {
          ...s.deviceEventCount,
          [key]: resolve(v, selectDeviceEventCount(key)(s)),
        },
      })),
    setIsSavingStripmanagement: (v) =>
      set((s) => ({ isSavingStripmanagement: resolve(v, s.isSavingStripmanagement) })),
    setIsWaiting: (key, v) =>
      set((s) => ({ isWaiting: { ...s.isWaiting, [key]: resolve(v, selectIsWaiting(key)(s)) } })),
    setIsSkipSelectThumbnail: (key, v) =>
      set((s) => ({
        isSkipSelectThumbnail: {
          ...s.isSkipSelectThumbnail,
          [key]: resolve(v, selectIsSkipSelectThumbnail(key)(s)),
        },
      })),
    setDailyArtifactCount: (key, v) =>
      set((s) => ({
        dailyArtifactCount: {
          ...s.dailyArtifactCount,
          [key]: resolve(v, selectDailyArtifactCount(key)(s)),
        },
      })),
    setSizeBeatThumbnailConfig: (v) =>
      set((s) => {
        const next = resolve(v, s.sizeBeatThumbnailConfig);
        persistSizeBeatThumbnailConfig(next, s.sizeBeatThumbnailConfig);
        return { sizeBeatThumbnailConfig: next };
      }),
    setMorphologyTemplate: (key, v) =>
      set((s) => ({
        morphologyTemplate: {
          ...s.morphologyTemplate,
          [key]: resolve(v, selectMorphologyTemplate(key)(s)),
        },
      })),
    setTemplateChanges: (key, v) =>
      set((s) => {
        const prev = selectTemplateChanges(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('templateChangesState', key, { recordUndo: true })(next, prev);
        return { templateChanges: { ...s.templateChanges, [key]: next } };
      }),
    setSelectedTemplate: (key, v) =>
      set((s) => ({
        selectedTemplate: {
          ...s.selectedTemplate,
          [key]: resolve(v, selectSelectedTemplate(key)(s)),
        },
      })),
    setSwapBeatChanges: (key, v) =>
      set((s) => {
        const prev = selectSwapBeatChanges(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('swapBeatChangesState', key)(next, prev);
        return { swapBeatChanges: { ...s.swapBeatChanges, [key]: next } };
      }),
    setOriginalTemplateOverview: (v) =>
      set((s) => ({ originalTemplateOverview: resolve(v, s.originalTemplateOverview) })),
    setInitTemplateOverview: (v) =>
      set((s) => ({ initTemplateOverview: resolve(v, s.initTemplateOverview) })),
    setTotalStrip: (v) => set((s) => ({ totalStrip: resolve(v, s.totalStrip) })),
    setListSelectedCells: (v) =>
      set((s) => ({ listSelectedCells: resolve(v, s.listSelectedCells) })),
    setLoadingBeatStatus: (v) =>
      set((s) => ({ loadingBeatStatus: resolve(v, s.loadingBeatStatus) })),
    setIsHideReviewedCell: (v) =>
      set((s) => {
        const next = resolve(v, s.isHideReviewedCell);
        persistIsHideReviewedCell(next, s.isHideReviewedCell);
        return { isHideReviewedCell: next };
      }),
    setDataHeatmapChart: (v) =>
      set((s) => {
        const next = resolve(v, s.dataHeatmapChart);
        persistDataHeatmapChart(next, s.dataHeatmapChart);
        return { dataHeatmapChart: next };
      }),
    setDataHeatmapChartStatus: (v) =>
      set((s) => {
        const next = resolve(v, s.dataHeatmapChartStatus);
        persistDataHeatmapChartStatus(next, s.dataHeatmapChartStatus);
        return { dataHeatmapChartStatus: next };
      }),
    setRrOverviewOrigin: (v) =>
      set((s) => {
        const next = resolve(v, s.rrOverviewOrigin);
        persistRrOverviewOrigin(next, s.rrOverviewOrigin);
        return { rrOverviewOrigin: next };
      }),
    setCombinedEventsInHeatmap: (v) =>
      set((s) => ({ combinedEventsInHeatmap: resolve(v, s.combinedEventsInHeatmap) })),
    setBulkAction: (key, v) =>
      set((s) => {
        const prev = selectBulkAction(key)(s);
        const next = resolve(v, prev);
        persistBulkAction(next, prev);
        return { bulkAction: { ...s.bulkAction, [key]: next } };
      }),
    setBulkActionSwap: (key, v) =>
      set((s) => {
        const prev = selectBulkActionSwap(key)(s);
        const next = resolve(v, prev);
        persistBulkActionSwap(next, prev);
        return { bulkActionSwap: { ...s.bulkActionSwap, [key]: next } };
      }),
    setBulkSwapBeats: (key, v) =>
      set((s) => {
        const prev = selectBulkSwapBeats(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('bulkSwapBeatsState', key)(next, prev);
        return { bulkSwapBeats: { ...s.bulkSwapBeats, [key]: next } };
      }),
    setResetSwapBeats: (key, v) =>
      set((s) => {
        const prev = selectResetSwapBeats(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('resetSwapBeatsState', key)(next, prev);
        return { resetSwapBeats: { ...s.resetSwapBeats, [key]: next } };
      }),
    setBulkActionByCell: (key, v) =>
      set((s) => {
        const prev = selectBulkActionByCell(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('bulkActionByCellState', key)(next, prev);
        return { bulkActionByCell: { ...s.bulkActionByCell, [key]: next } };
      }),
    setViewBeatsSource: (v) => set((s) => ({ viewBeatsSource: resolve(v, s.viewBeatsSource) })),
    setBulkReviewedBeats: (v) =>
      set((s) => {
        const next = resolve(v, s.bulkReviewedBeats);
        persistBulkReviewedBeats(next, s.bulkReviewedBeats);
        return { bulkReviewedBeats: next };
      }),
    setReviewedBeatsChanges: (v) =>
      set((s) => {
        const next = resolve(v, s.reviewedBeatsChanges);
        persistReviewedBeatsChanges(next, s.reviewedBeatsChanges);
        return { reviewedBeatsChanges: next };
      }),
    setResetBulkReviewedBeats: (v) =>
      set((s) => {
        const next = resolve(v, s.resetBulkReviewedBeats);
        persistResetBulkReviewedBeats(next, s.resetBulkReviewedBeats);
        return { resetBulkReviewedBeats: next };
      }),
    setVisibleReviewedFilter: (v) =>
      set((s) => {
        const next = resolve(v, s.visibleReviewedFilter);
        persistVisibleReviewedFilter(next, s.visibleReviewedFilter);
        return { visibleReviewedFilter: next };
      }),
    setBeatsSortType: (v) =>
      set((s) => {
        const next = resolve(v, s.beatsSortType);
        persistBeatsSortType(next, s.beatsSortType);
        return { beatsSortType: next };
      }),
    setIsLoadedIndexDB: (v) => set((s) => ({ isLoadedIndexDB: resolve(v, s.isLoadedIndexDB) })),
    setReportInfo: (v) =>
      set((s) => {
        const next = resolve(v, s.reportInfo);
        persistReportInfo(next, s.reportInfo);
        return { reportInfo: next };
      }),
    setIsStartedAiProcessing: (v) =>
      set((s) => ({ isStartedAiProcessing: resolve(v, s.isStartedAiProcessing) })),
    setIsOpenProcessingLimit: (v) =>
      set((s) => ({ isOpenProcessingLimit: resolve(v, s.isOpenProcessingLimit) })),
    setLimitProcessingStudy: (v) =>
      set((s) => ({ limitProcessingStudy: resolve(v, s.limitProcessingStudy) })),
    setIsShowTabHeader: (v) =>
      set((s) => {
        const next = resolve(v, s.isShowTabHeader);
        persistIsShowTabHeader(next, s.isShowTabHeader);
        return { isShowTabHeader: next };
      }),
    setIsPushQueue: (v) => set((s) => ({ isPushQueue: resolve(v, s.isPushQueue) })),
    setIsCompletedDownloadBeat: (v) =>
      set((s) => ({ isCompletedDownloadBeat: resolve(v, s.isCompletedDownloadBeat) })),
    setIsDownloadingBeatFile: (v) =>
      set((s) => ({ isDownloadingBeatFile: resolve(v, s.isDownloadingBeatFile) })),
    setListDownloadFile: (v) => set((s) => ({ listDownloadFile: resolve(v, s.listDownloadFile) })),
    setProfileAfibAvgHr: (v) =>
      set((s) => {
        const next = resolve(v, s.profileAfibAvgHr);
        persistProfileAfibAvgHr(next, s.profileAfibAvgHr);
        return { profileAfibAvgHr: next };
      }),
    setIsDisabledBtn: (v) => set((s) => ({ isDisabledBtn: resolve(v, s.isDisabledBtn) })),
    setIsUndoDisabled: (v) => set((s) => ({ isUndoDisabled: resolve(v, s.isUndoDisabled) })),
    setIsRedoDisabled: (v) => set((s) => ({ isRedoDisabled: resolve(v, s.isRedoDisabled) })),
    setDateList: (v) => set((s) => ({ dateList: resolve(v, s.dateList) })),
    setSpecialEventTypes: (v) =>
      set((s) => ({ specialEventTypes: resolve(v, s.specialEventTypes) })),
    setActionSnapshot: (v) => set((s) => ({ actionSnapshot: resolve(v, s.actionSnapshot) })),
    setIsRenderViewerEcg: (v) =>
      set((s) => {
        const next = resolve(v, s.isRenderViewerEcg);
        persistIsRenderViewerEcg(next, s.isRenderViewerEcg);
        return { isRenderViewerEcg: next };
      }),
    setSizeEventThumbnailConfig: (v) =>
      set((s) => {
        const next = resolve(v, s.sizeEventThumbnailConfig);
        persistSizeEventThumbnailConfig(next, s.sizeEventThumbnailConfig);
        return { sizeEventThumbnailConfig: next };
      }),
    setHighPassThumbnail: (v) =>
      set((s) => {
        const next = resolve(v, s.highPassThumbnail);
        persistHighPassThumbnail(next, s.highPassThumbnail);
        return { highPassThumbnail: next };
      }),
    setGainThumbnail: (v) =>
      set((s) => {
        const next = resolve(v, s.gainThumbnail);
        persistGainThumbnail(next, s.gainThumbnail);
        return { gainThumbnail: next };
      }),
    setChannelsThumbnail: (v) =>
      set((s) => {
        const next = resolve(v, s.channelsThumbnail);
        persistChannelsThumbnail(next, s.channelsThumbnail);
        return { channelsThumbnail: next };
      }),
    setAiChannel: (key, v) =>
      set((s) => {
        const prev = selectAiChannel(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('aiChannelState', key)(next, prev);
        return { aiChannel: { ...s.aiChannel, [key]: next } };
      }),
    setEcgObject: (key, v) =>
      set((s) => ({
        ecgObject: { ...s.ecgObject, [key]: resolve(v, selectEcgObject(key)(s)) },
      })),
    setReloadEcgViewer: (key, v) =>
      set((s) => ({
        reloadEcgViewer: { ...s.reloadEcgViewer, [key]: resolve(v, selectReloadEcgViewer(key)(s)) },
      })),
    setIsLoadingEcgViewer: (key, v) =>
      set((s) => ({
        isLoadingEcgViewer: {
          ...s.isLoadingEcgViewer,
          [key]: resolve(v, selectIsLoadingEcgViewer(key)(s)),
        },
      })),
    setPreventReloadEcg: (key, v) =>
      set((s) => ({
        preventReloadEcg: {
          ...s.preventReloadEcg,
          [key]: resolve(v, selectPreventReloadEcg(key)(s)),
        },
      })),
    setEcgDataMap: (v) => set((s) => ({ ecgDataMap: resolve(v, s.ecgDataMap) })),
    setPickDateAction: (key, v) =>
      set((s) => ({
        pickDateAction: { ...s.pickDateAction, [key]: resolve(v, selectPickDateAction(key)(s)) },
      })),
    setPickedStartDate: (key, v) =>
      set((s) => ({
        pickedStartDate: {
          ...s.pickedStartDate,
          [key]: resolve(v, selectPickedStartDate(key)(s)),
        },
      })),
    setPickedEndDate: (key, v) =>
      set((s) => ({
        pickedEndDate: { ...s.pickedEndDate, [key]: resolve(v, selectPickedEndDate(key)(s)) },
      })),
    setPickedBookmarkDate: (key, v) =>
      set((s) => {
        const prev = selectPickedBookmarkDate(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('pickedBookmarkDateState', key)(next, prev);
        return { pickedBookmarkDate: { ...s.pickedBookmarkDate, [key]: next } };
      }),
    setJumpDate: (key, v) =>
      set((s) => ({ jumpDate: { ...s.jumpDate, [key]: resolve(v, selectJumpDate(key)(s)) } })),
    setJumpTime: (key, v) =>
      set((s) => ({ jumpTime: { ...s.jumpTime, [key]: resolve(v, selectJumpTime(key)(s)) } })),
    setJumpPortion: (key, v) =>
      set((s) => ({
        jumpPortion: { ...s.jumpPortion, [key]: resolve(v, selectJumpPortion(key)(s)) },
      })),
    setSelectedDateValue: (key, v) =>
      set((s) => {
        const prev = selectSelectedDateValue(key)(s);
        const next = resolve(v, prev);
        if (key !== KEY_RECOIL.STRIP_MANAGEMENT) {
          createKeyedFieldPersistence('selectedDateValueState', key)(next, prev);
        }
        return { selectedDateValue: { ...s.selectedDateValue, [key]: next } };
      }),
    setDailyCount: (key, v) =>
      set((s) => ({
        dailyCount: { ...s.dailyCount, [key]: resolve(v, selectDailyCount(key)(s)) },
      })),
    setActiveTab: (key, v) =>
      set((s) => {
        const prev = selectActiveTab(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('activeTabState', key)(next, prev);
        return { activeTab: { ...s.activeTab, [key]: next } };
      }),
    setPageIndex: (key, v) =>
      set((s) => {
        const prev = selectPageIndex(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('pageIndexState', key)(next, prev);
        return { pageIndex: { ...s.pageIndex, [key]: next } };
      }),
    setIsActiveTab: (key, v) =>
      set((s) => ({
        isActiveTab: { ...s.isActiveTab, [key]: resolve(v, selectIsActiveTab(key)(s)) },
      })),
    setIsEmptyData: (key, v) =>
      set((s) => ({
        isEmptyData: { ...s.isEmptyData, [key]: resolve(v, selectIsEmptyData(key)(s)) },
      })),
    setReloadHrChart: (key, v) =>
      set((s) => {
        const prev = selectReloadHrChart(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('reloadHrChartState', key)(next, prev);
        return { reloadHrChart: { ...s.reloadHrChart, [key]: next } };
      }),
    setIsLoadingHrChart: (key, v) =>
      set((s) => ({
        isLoadingHrChart: {
          ...s.isLoadingHrChart,
          [key]: resolve(v, selectIsLoadingHrChart(key)(s)),
        },
      })),
    setIsLoadingHrChartOptions: (key, v) =>
      set((s) => ({
        isLoadingHrChartOptions: {
          ...s.isLoadingHrChartOptions,
          [key]: resolve(v, selectIsLoadingHrChartOptions(key)(s)),
        },
      })),
    setEventFilter: (key, v) =>
      set((s) => {
        const prev = selectEventFilter(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('eventFilterState', key)(next, prev);
        return { eventFilter: { ...s.eventFilter, [key]: next } };
      }),
    setSpecialEventCount: (key, v) =>
      set((s) => ({
        specialEventCount: {
          ...s.specialEventCount,
          [key]: resolve(v, selectSpecialEventCount(key)(s)),
        },
      })),
    setIsShowArtifactEvent: (key, v) =>
      set((s) => ({
        isShowArtifactEvent: {
          ...s.isShowArtifactEvent,
          [key]: resolve(v, selectIsShowArtifactEvent(key)(s)),
        },
      })),
    setIsShowAfibEvent: (key, v) =>
      set((s) => ({
        isShowAfibEvent: {
          ...s.isShowAfibEvent,
          [key]: resolve(v, selectIsShowAfibEvent(key)(s)),
        },
      })),
    setIsShowOtherEvent: (key, v) =>
      set((s) => ({
        isShowOtherEvent: {
          ...s.isShowOtherEvent,
          [key]: resolve(v, selectIsShowOtherEvent(key)(s)),
        },
      })),
    setControlHrSummaryModal: (v) =>
      set((s) => {
        const prev = s.controlHrSummaryModal;
        const next = resolve(v, prev);
        persistControlHrSummaryModal(next, prev);
        return { controlHrSummaryModal: next };
      }),
    resetControlHrSummaryModal: () =>
      set((s) => {
        const prev = s.controlHrSummaryModal;
        persistControlHrSummaryModal(DEFAULT_CONTROL_HR_SUMMARY_MODAL_VALUE, prev);
        return { controlHrSummaryModal: DEFAULT_CONTROL_HR_SUMMARY_MODAL_VALUE };
      }),
    setSelectedStrip: (key, v) =>
      set((s) => {
        const prev = selectSelectedStrip(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('selectedStripState', key)(next, prev);
        return { selectedStrip: { ...s.selectedStrip, [key]: next } };
      }),
    setSelectedMultipleStrip: (key, v) =>
      set((s) => ({
        selectedMultipleStrip: {
          ...s.selectedMultipleStrip,
          [key]: resolve(v, selectSelectedMultipleStrip(key)(s)),
        },
      })),
    setIsLoadingStrip: (key, v) =>
      set((s) => ({
        isLoadingStrip: { ...s.isLoadingStrip, [key]: resolve(v, selectIsLoadingStrip(key)(s)) },
      })),
    setSelectedColumnInfo: (key, v) =>
      set((s) => ({
        selectedColumnInfo: {
          ...s.selectedColumnInfo,
          [key]: resolve(v, selectSelectedColumnInfo(key)(s)),
        },
      })),
    setPrevColumnInfo: (key, v) =>
      set((s) => ({
        prevColumnInfo: { ...s.prevColumnInfo, [key]: resolve(v, selectPrevColumnInfo(key)(s)) },
      })),
    setNextColumnInfo: (key, v) =>
      set((s) => ({
        nextColumnInfo: { ...s.nextColumnInfo, [key]: resolve(v, selectNextColumnInfo(key)(s)) },
      })),
    setSelectedEventIndex: (key, v) =>
      set((s) => ({
        selectedEventIndex: {
          ...s.selectedEventIndex,
          [key]: resolve(v, selectSelectedEventIndex(key)(s)),
        },
      })),
    setSelectedEvent: (key, v) =>
      set((s) => {
        const prev = selectSelectedEvent(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('selectedEventState', key)(next, prev);
        return { selectedEvent: { ...s.selectedEvent, [key]: next } };
      }),
    setSelectedEventInfo: (key, v) =>
      set((s) => ({
        selectedEventInfo: {
          ...s.selectedEventInfo,
          [key]: resolve(v, selectSelectedEventInfo(key)(s)),
        },
      })),
    setSelectedEventInfoIndex: (key, v) =>
      set((s) => {
        const prev = selectSelectedEventInfoIndex(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('selectedEventInfoIndexState', key)(next, prev);
        return { selectedEventInfoIndex: { ...s.selectedEventInfoIndex, [key]: next } };
      }),
    setHolterEvents: (key, v) =>
      set((s) => {
        const prev = selectHolterEvents(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('holterEventsState', key)(next, prev);
        return { holterEvents: { ...s.holterEvents, [key]: next } };
      }),
    setCombinedEvents: (key, v) =>
      set((s) => ({
        combinedEvents: { ...s.combinedEvents, [key]: resolve(v, selectCombinedEvents(key)(s)) },
      })),
    setMinuteInfoEvents: (key, v) =>
      set((s) => ({
        minuteInfoEvents: {
          ...s.minuteInfoEvents,
          [key]: resolve(v, selectMinuteInfoEvents(key)(s)),
        },
      })),
    setIsOpenModalEditStrip: (key, v) =>
      set((s) => ({
        isOpenModalEditStrip: {
          ...s.isOpenModalEditStrip,
          [key]: resolve(v, selectIsOpenModalEditStrip(key)(s)),
        },
      })),
    setControlModal: (key, v) =>
      set((s) => {
        const prev = selectControlModal(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('controlModalState', key)(next, prev);
        return { controlModal: { ...s.controlModal, [key]: next } };
      }),
    resetControlModal: (key) =>
      set((s) => {
        const prev = selectControlModal(key)(s);
        createKeyedFieldPersistence('controlModalState', key)(DEFAULT_CONTROL_MODAL_VALUE, prev);
        return { controlModal: { ...s.controlModal, [key]: DEFAULT_CONTROL_MODAL_VALUE } };
      }),
    setEventChanges: (key, v) =>
      set((s) => {
        const prev = selectEventChanges(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('eventChangesState', key)(next, prev);
        return { eventChanges: { ...s.eventChanges, [key]: next } };
      }),
    setEventNews: (key, v) =>
      set((s) => {
        const prev = selectEventNews(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('eventNewsState', key)(next, prev);
        return { eventNews: { ...s.eventNews, [key]: next } };
      }),
    setEventOthers: (key, v) =>
      set((s) => {
        const prev = selectEventOthers(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('eventOthersState', key)(next, prev);
        return { eventOthers: { ...s.eventOthers, [key]: next } };
      }),
    setBeatChanges: (key, v) =>
      set((s) => {
        const prev = selectBeatChanges(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('beatChangesState', key)(next, prev);
        return { beatChanges: { ...s.beatChanges, [key]: next } };
      }),
    setRhythmEventCount: (v) => set((s) => ({ rhythmEventCount: resolve(v, s.rhythmEventCount) })),
    setBeatEventCount: (v) => set((s) => ({ beatEventCount: resolve(v, s.beatEventCount) })),
    setGroupEventChanges: (key, v) =>
      set((s) => {
        const prev = selectGroupEventChanges(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('groupEventChangesState', key, { recordUndo: true })(
          next,
          prev,
        );
        return { groupEventChanges: { ...s.groupEventChanges, [key]: next } };
      }),
    setEventOptions: (key, v) =>
      set((s) => {
        const prev = selectEventOptions(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('eventOptionsState', key, { recordUndo: true })(next, prev);
        return { eventOptions: { ...s.eventOptions, [key]: next } };
      }),
    setBeatOptions: (key, v) =>
      set((s) => {
        const prev = selectBeatOptions(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('beatOptionsState', key, { recordUndo: true })(next, prev);
        return { beatOptions: { ...s.beatOptions, [key]: next } };
      }),
    setBulkBeatsChanges: (key, v) =>
      set((s) => {
        const prev = selectBulkBeatsChanges(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('bulkBeatsChangesState', key)(next, prev);
        return { bulkBeatsChanges: { ...s.bulkBeatsChanges, [key]: next } };
      }),
    setResetBulkBeatsChanges: (key, v) =>
      set((s) => {
        const prev = selectResetBulkBeatsChanges(key)(s);
        const next = resolve(v, prev);
        createKeyedFieldPersistence('resetBulkBeatsChangesState', key)(next, prev);
        return { resetBulkBeatsChanges: { ...s.resetBulkBeatsChanges, [key]: next } };
      }),
    setEcgBookmarks: (v) =>
      set((s) => {
        const prev = s.ecgBookmarks;
        const next = resolve(v, prev);
        persistEcgBookmarks(next, prev);
        return { ecgBookmarks: next };
      }),
    setUpdatedDailySummaries: (v) =>
      set((s) => {
        const prev = s.updatedDailySummaries;
        const next = resolve(v, prev);
        persistUpdatedDailySummaries(next, prev);
        return { updatedDailySummaries: next };
      }),
  },
}));

/**
 * Supplies the stand-in value for a single persisted report field whose load rejected. Yields undefined so the hydration spread leaves the store's existing value untouched.
 *
 * @returns Undefined, standing in for the value that could not be loaded.
 */
const hydrateFieldFailed = (): undefined => undefined;
/**
 * Supplies the stand-in value for a per-tab persisted record whose load rejected. Yields an empty record so the hydration spread finds no tab keys to merge.
 *
 * @returns An empty record of per-tab values.
 */
const hydrateKeyedFieldFailed = (): Record<string, never> => ({});
/**
 * Was 66 sequential `await` steps (226 underlying IndexedDB reads across them,
 * since each keyed step already fans out over `ALL_TAB_KEYS` internally) — every
 * step waited for the previous one, and each of the 66 `setState` calls landed in
 * its own post-`await` tick, so React committed 66 separate times during mount and
 * the window where a fresher write could get clobbered by a late hydrate widened
 * with every step. Now: every read fires in parallel, and — since a single failed
 * IndexedDB read must not blank out or abort the other 65 — each promise swallows
 * its own rejection via `.catch()` before entering `Promise.all` (equivalent to
 * `Promise.allSettled`, but keeps each element's own generic type instead of a
 * `PromiseSettledResult<T>` union), falling back to `undefined` (plain fields) or
 * `{}` (keyed fields, so `Object.keys(...).length > 0` below still behaves) so a
 * failed field is simply skipped rather than poisoning the whole hydrate. All 66
 * results are then applied in ONE `setState`, so mount renders exactly once.
 */
/**
 * Loads the persisted report session back into the combined report store, covering findings, thumbnail configs, heatmap data, bulk-action state and the per-tab event, beat, filter and pagination records. Awaits one loadPersistedValue or loadPersistedKeyedRecord call per field, swallowing each rejection through hydrateFieldFailed or hydrateKeyedFieldFailed, then applies a single setState spreading in only the fields and tab keys that came back.
 *
 * @returns A promise resolving once every persisted field has been read and merged into the store.
 */
export const hydrateReportStore = async (): Promise<void> => {
  const [
    findings,
    findingsChange,
    facilityNote,
    viewHrMode,
    dailySummariesCombine,
    maxHrsInDate,
    minHrsInDate,
    ecgBookmarkDeleteIds,
    jumpDateStatus,
    sizeStripManagementThumbnailConfig,
    sizeStripDeviceThumbnailConfig,
    selectedEcgDisclosure,
    ecgDisclosureChange,
    deviceEventData,
    changeStripData,
    stripFilter,
    deviceStripFilter,
    sizeBeatThumbnailConfig,
    templateChanges,
    swapBeatChanges,
    isHideReviewedCell,
    dataHeatmapChart,
    dataHeatmapChartStatus,
    rrOverviewOrigin,
    bulkAction,
    bulkActionSwap,
    bulkSwapBeats,
    resetSwapBeats,
    bulkActionByCell,
    bulkReviewedBeats,
    reviewedBeatsChanges,
    resetBulkReviewedBeats,
    visibleReviewedFilter,
    beatsSortType,
    reportInfo,
    isShowTabHeader,
    profileAfibAvgHr,
    isRenderViewerEcg,
    sizeEventThumbnailConfig,
    highPassThumbnail,
    gainThumbnail,
    channelsThumbnail,
    aiChannel,
    pickedBookmarkDate,
    selectedDateValue,
    activeTab,
    pageIndex,
    reloadHrChart,
    eventFilter,
    controlHrSummaryModal,
    selectedStrip,
    selectedEvent,
    selectedEventInfoIndex,
    holterEvents,
    controlModal,
    eventChanges,
    eventNews,
    eventOthers,
    beatChanges,
    groupEventChanges,
    eventOptions,
    beatOptions,
    bulkBeatsChanges,
    resetBulkBeatsChanges,
    ecgBookmarks,
    updatedDailySummaries,
  ] = await Promise.all([
    loadPersistedValue<String>('findingsState').catch(hydrateFieldFailed),
    loadPersistedValue<String>('findingsChangeState').catch(hydrateFieldFailed),
    loadPersistedValue<String>('facilityNote').catch(hydrateFieldFailed),
    loadPersistedKeyedRecord<ViewHrMode>('viewHrModeState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedValue<Array<IDailySummary>>('dailySummariesCombineState').catch(
      hydrateFieldFailed,
    ),
    loadPersistedValue<Array<IHrsInDate>>('maxHrsInDateState').catch(hydrateFieldFailed),
    loadPersistedValue<Array<IHrsInDate>>('minHrsInDateState').catch(hydrateFieldFailed),
    loadPersistedValue<Array<string>>('ecgBookmarkDeleteIdsState').catch(hydrateFieldFailed),
    loadPersistedKeyedRecord<TJumpDateStatus>('jumpDateStatusState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedValue<ISizeThumbnailConfig>('sizeStripManagementThumbnailConfigState').catch(
      hydrateFieldFailed,
    ),
    loadPersistedValue<ISizeThumbnailConfig>('sizeStripDeviceThumbnailConfigState').catch(
      hydrateFieldFailed,
    ),
    loadPersistedValue<IEcgDisclosure>('selectedEcgDisclosureState').catch(hydrateFieldFailed),
    loadPersistedValue<IEcgDisclosure>('ecgDisclosureChangeState').catch(hydrateFieldFailed),
    loadPersistedKeyedRecord<IDeviceEventData>('deviceEventDataState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<Array<any>>('changeStripDataState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<IStripFilter>('stripFilterState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<ISdeviceStripFilter>('deviceStripFilterState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedValue<ISizeThumbnailConfig>('sizeBeatThumbnailConfigState').catch(
      hydrateFieldFailed,
    ),
    loadPersistedKeyedRecord<Array<any>>('templateChangesState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<Array<any>>('swapBeatChangesState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedValue<boolean>('isHideReviewedCellState').catch(hydrateFieldFailed),
    loadPersistedValue<TDataHeatmapChart>('dataHeatmapChartState').catch(hydrateFieldFailed),
    loadPersistedValue<DataHeatmapChart<TDataHeatmapStatus>>('dataHeatmapChartStatusState').catch(
      hydrateFieldFailed,
    ),
    loadPersistedValue<DataHeatmapChart<TRrOverview>>('rrOverviewOriginState').catch(
      hydrateFieldFailed,
    ),
    loadPersistedValue<TBulkAction>('bulkActionState').catch(hydrateFieldFailed),
    loadPersistedValue<any>('bulkActionSwapState').catch(hydrateFieldFailed),
    loadPersistedKeyedRecord<any>('bulkSwapBeatsState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<Array<any>>('resetSwapBeatsState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<TBulkAction>('bulkActionByCellState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedValue<any>('bulkReviewedBeatsState').catch(hydrateFieldFailed),
    loadPersistedValue<any>('reviewedBeatsChangesState').catch(hydrateFieldFailed),
    loadPersistedValue<any>('resetBulkReviewedBeatsState').catch(hydrateFieldFailed),
    loadPersistedValue<string>('visibleReviewedFilterState').catch(hydrateFieldFailed),
    loadPersistedValue<string>('beatsSortTypeState').catch(hydrateFieldFailed),
    loadPersistedValue<IReportInfo>('reportInfoState').catch(hydrateFieldFailed),
    loadPersistedValue<boolean>('isShowTabHeaderState').catch(hydrateFieldFailed),
    loadPersistedValue<any>('profileAfibAvgHrState').catch(hydrateFieldFailed),
    loadPersistedValue<boolean>('isRenderViewerEcgState').catch(hydrateFieldFailed),
    loadPersistedValue<ISizeThumbnailConfig>('sizeEventThumbnailConfigState').catch(
      hydrateFieldFailed,
    ),
    loadPersistedValue<number>('highPassThumbnailState').catch(hydrateFieldFailed),
    loadPersistedValue<number>('gainThumbnailState').catch(hydrateFieldFailed),
    loadPersistedValue<Array<number>>('channelsThumbnailState').catch(hydrateFieldFailed),
    loadPersistedKeyedRecord<TAiChannel | null>('aiChannelState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<string | null>('pickedBookmarkDateState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<string | null>(
      'selectedDateValueState',
      ALL_TAB_KEYS.filter((key) => key !== KEY_RECOIL.STRIP_MANAGEMENT),
    ).catch(hydrateKeyedFieldFailed),
    loadPersistedKeyedRecord<TBeatHrActiveTab | TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM>(
      'activeTabState',
      ALL_TAB_KEYS,
    ).catch(hydrateKeyedFieldFailed),
    loadPersistedKeyedRecord<IPageIndex>('pageIndexState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<number>('reloadHrChartState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<IEventFilter>('eventFilterState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedValue<IControlModalHrSummary>('controlHrSummaryModalState').catch(
      hydrateFieldFailed,
    ),
    loadPersistedKeyedRecord<IEvent | IBeat | IEcgBookmark | null>(
      'selectedStripState',
      ALL_TAB_KEYS,
    ).catch(hydrateKeyedFieldFailed),
    loadPersistedKeyedRecord<Array<IEvent | IPoint>>('selectedEventState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<number>('selectedEventInfoIndexState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<Array<IEvent>>('holterEventsState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<IControlModal>('controlModalState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<Array<IEvent>>('eventChangesState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<Array<IEvent>>('eventNewsState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<Array<IEventOther>>('eventOthersState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<Array<IBeatChange>>('beatChangesState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<Array<IGroupEventChanges>>(
      'groupEventChangesState',
      ALL_TAB_KEYS,
    ).catch(hydrateKeyedFieldFailed),
    loadPersistedKeyedRecord<any>('eventOptionsState', ALL_TAB_KEYS).catch(hydrateKeyedFieldFailed),
    loadPersistedKeyedRecord<any>('beatOptionsState', ALL_TAB_KEYS).catch(hydrateKeyedFieldFailed),
    loadPersistedKeyedRecord<any>('bulkBeatsChangesState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedKeyedRecord<any>('resetBulkBeatsChangesState', ALL_TAB_KEYS).catch(
      hydrateKeyedFieldFailed,
    ),
    loadPersistedValue<Array<IEcgBookmark>>('ecgBookmarksState').catch(hydrateFieldFailed),
    loadPersistedValue<Array<IDailySummary>>('updatedDailySummariesState').catch(
      hydrateFieldFailed,
    ),
  ]);
  useReportStore.setState((s) => ({
    ...(findings !== undefined ? { findings } : {}),
    ...(findingsChange !== undefined ? { findingsChange } : {}),
    ...(facilityNote !== undefined ? { facilityNote } : {}),
    ...(Object.keys(viewHrMode).length > 0
      ? { viewHrMode: { ...s.viewHrMode, ...viewHrMode } }
      : {}),
    ...(dailySummariesCombine !== undefined ? { dailySummariesCombine } : {}),
    ...(maxHrsInDate !== undefined ? { maxHrsInDate } : {}),
    ...(minHrsInDate !== undefined ? { minHrsInDate } : {}),
    ...(ecgBookmarkDeleteIds !== undefined ? { ecgBookmarkDeleteIds } : {}),
    ...(Object.keys(jumpDateStatus).length > 0
      ? {
          jumpDateStatus: { ...s.jumpDateStatus, ...jumpDateStatus },
        }
      : {}),
    ...(sizeStripManagementThumbnailConfig !== undefined
      ? { sizeStripManagementThumbnailConfig }
      : {}),
    ...(sizeStripDeviceThumbnailConfig !== undefined ? { sizeStripDeviceThumbnailConfig } : {}),
    ...(selectedEcgDisclosure !== undefined ? { selectedEcgDisclosure } : {}),
    ...(ecgDisclosureChange !== undefined ? { ecgDisclosureChange } : {}),
    ...(Object.keys(deviceEventData).length > 0
      ? {
          deviceEventData: { ...s.deviceEventData, ...deviceEventData },
        }
      : {}),
    ...(Object.keys(changeStripData).length > 0
      ? {
          changeStripData: { ...s.changeStripData, ...changeStripData },
        }
      : {}),
    ...(Object.keys(stripFilter).length > 0
      ? { stripFilter: { ...s.stripFilter, ...stripFilter } }
      : {}),
    ...(Object.keys(deviceStripFilter).length > 0
      ? {
          deviceStripFilter: { ...s.deviceStripFilter, ...deviceStripFilter },
        }
      : {}),
    ...(sizeBeatThumbnailConfig !== undefined ? { sizeBeatThumbnailConfig } : {}),
    ...(Object.keys(templateChanges).length > 0
      ? {
          templateChanges: { ...s.templateChanges, ...templateChanges },
        }
      : {}),
    ...(Object.keys(swapBeatChanges).length > 0
      ? {
          swapBeatChanges: { ...s.swapBeatChanges, ...swapBeatChanges },
        }
      : {}),
    ...(isHideReviewedCell !== undefined ? { isHideReviewedCell } : {}),
    ...(dataHeatmapChart !== undefined ? { dataHeatmapChart } : {}),
    ...(dataHeatmapChartStatus !== undefined ? { dataHeatmapChartStatus } : {}),
    ...(rrOverviewOrigin !== undefined ? { rrOverviewOrigin } : {}),
    ...(bulkAction !== undefined
      ? {
          bulkAction: {
            ...s.bulkAction,
            ...Object.fromEntries(ALL_TAB_KEYS.map((k) => [k, bulkAction])),
          },
        }
      : {}),
    ...(bulkActionSwap !== undefined
      ? {
          bulkActionSwap: {
            ...s.bulkActionSwap,
            ...Object.fromEntries(ALL_TAB_KEYS.map((k) => [k, bulkActionSwap])),
          },
        }
      : {}),
    ...(Object.keys(bulkSwapBeats).length > 0
      ? {
          bulkSwapBeats: { ...s.bulkSwapBeats, ...bulkSwapBeats },
        }
      : {}),
    ...(Object.keys(resetSwapBeats).length > 0
      ? {
          resetSwapBeats: { ...s.resetSwapBeats, ...resetSwapBeats },
        }
      : {}),
    ...(Object.keys(bulkActionByCell).length > 0
      ? {
          bulkActionByCell: { ...s.bulkActionByCell, ...bulkActionByCell },
        }
      : {}),
    ...(bulkReviewedBeats !== undefined ? { bulkReviewedBeats } : {}),
    ...(reviewedBeatsChanges !== undefined ? { reviewedBeatsChanges } : {}),
    ...(resetBulkReviewedBeats !== undefined ? { resetBulkReviewedBeats } : {}),
    ...(visibleReviewedFilter !== undefined ? { visibleReviewedFilter } : {}),
    ...(beatsSortType !== undefined ? { beatsSortType } : {}),
    ...(reportInfo !== undefined ? { reportInfo } : {}),
    ...(isShowTabHeader !== undefined ? { isShowTabHeader } : {}),
    ...(profileAfibAvgHr !== undefined ? { profileAfibAvgHr } : {}),
    ...(isRenderViewerEcg !== undefined ? { isRenderViewerEcg } : {}),
    ...(sizeEventThumbnailConfig !== undefined ? { sizeEventThumbnailConfig } : {}),
    ...(highPassThumbnail !== undefined ? { highPassThumbnail } : {}),
    ...(gainThumbnail !== undefined ? { gainThumbnail } : {}),
    ...(channelsThumbnail !== undefined ? { channelsThumbnail } : {}),
    ...(Object.keys(aiChannel).length > 0 ? { aiChannel: { ...s.aiChannel, ...aiChannel } } : {}),
    ...(Object.keys(pickedBookmarkDate).length > 0
      ? {
          pickedBookmarkDate: { ...s.pickedBookmarkDate, ...pickedBookmarkDate },
        }
      : {}),
    ...(Object.keys(selectedDateValue).length > 0
      ? {
          selectedDateValue: { ...s.selectedDateValue, ...selectedDateValue },
        }
      : {}),
    ...(Object.keys(activeTab).length > 0 ? { activeTab: { ...s.activeTab, ...activeTab } } : {}),
    ...(Object.keys(pageIndex).length > 0 ? { pageIndex: { ...s.pageIndex, ...pageIndex } } : {}),
    ...(Object.keys(reloadHrChart).length > 0
      ? { reloadHrChart: { ...s.reloadHrChart, ...reloadHrChart } }
      : {}),
    ...(Object.keys(eventFilter).length > 0
      ? { eventFilter: { ...s.eventFilter, ...eventFilter } }
      : {}),
    ...(controlHrSummaryModal !== undefined ? { controlHrSummaryModal } : {}),
    ...(Object.keys(selectedStrip).length > 0
      ? { selectedStrip: { ...s.selectedStrip, ...selectedStrip } }
      : {}),
    ...(Object.keys(selectedEvent).length > 0
      ? { selectedEvent: { ...s.selectedEvent, ...selectedEvent } }
      : {}),
    ...(Object.keys(selectedEventInfoIndex).length > 0
      ? {
          selectedEventInfoIndex: { ...s.selectedEventInfoIndex, ...selectedEventInfoIndex },
        }
      : {}),
    ...(Object.keys(holterEvents).length > 0
      ? { holterEvents: { ...s.holterEvents, ...holterEvents } }
      : {}),
    ...(Object.keys(controlModal).length > 0
      ? { controlModal: { ...s.controlModal, ...controlModal } }
      : {}),
    ...(Object.keys(eventChanges).length > 0
      ? { eventChanges: { ...s.eventChanges, ...eventChanges } }
      : {}),
    ...(Object.keys(eventNews).length > 0 ? { eventNews: { ...s.eventNews, ...eventNews } } : {}),
    ...(Object.keys(eventOthers).length > 0
      ? { eventOthers: { ...s.eventOthers, ...eventOthers } }
      : {}),
    ...(Object.keys(beatChanges).length > 0
      ? { beatChanges: { ...s.beatChanges, ...beatChanges } }
      : {}),
    ...(Object.keys(groupEventChanges).length > 0
      ? {
          groupEventChanges: { ...s.groupEventChanges, ...groupEventChanges },
        }
      : {}),
    ...(Object.keys(eventOptions).length > 0
      ? { eventOptions: { ...s.eventOptions, ...eventOptions } }
      : {}),
    ...(Object.keys(beatOptions).length > 0
      ? { beatOptions: { ...s.beatOptions, ...beatOptions } }
      : {}),
    ...(Object.keys(bulkBeatsChanges).length > 0
      ? {
          bulkBeatsChanges: { ...s.bulkBeatsChanges, ...bulkBeatsChanges },
        }
      : {}),
    ...(Object.keys(resetBulkBeatsChanges).length > 0
      ? {
          resetBulkBeatsChanges: { ...s.resetBulkBeatsChanges, ...resetBulkBeatsChanges },
        }
      : {}),
    ...(ecgBookmarks !== undefined ? { ecgBookmarks } : {}),
    ...(updatedDailySummaries !== undefined ? { updatedDailySummaries } : {}),
  }));
};

/**
 * Faithful Zustand replacement for the old Recoil `gotoSnapshot` rollback used by
 * `debugObserver.ts`. Restores every field via its OWN action (not a raw `setState`),
 * so each field's already-ported persistence/undo logic (`createFieldPersistence`/
 * `createKeyedFieldPersistence`, called inside every action below) reruns exactly as
 * Recoil's `handleEffect` onSet did when `gotoSnapshot` touched an atom — same as a
 * real edit, so IndexedDB and undo-history stay in sync with the rollback.
 *
 * For per-tab keyed fields, also mirrors `gotoSnapshot`'s other half: a tab key that
 * exists in the live store but didn't exist yet when `saved` was captured (e.g. a tab
 * opened for the first time after the last safe checkpoint) gets reset to that
 * field's own default — Recoil's `gotoSnapshot` does this for any atomFamily member
 * absent from the snapshot (`next.has(key) ? next.get(key) : DEFAULT_VALUE`).
 */
/**
 * Rolls the combined report store back to a previously captured state, driving the report's undo and redo. Pushes every saved scalar field through its setter, then for each per-tab record writes back each saved tab key and resets any key present in the live state but absent from the snapshot to that field's default.
 *
 * @param saved - The captured report state to restore, including the action setters used to apply it.
 * @returns Nothing.
 */
export const restoreReportSnapshot = (saved: ReportState): void => {
  const current = useReportStore.getState();
  const { actions } = saved;
  actions.setReportData(saved.reportData);
  actions.setFindings(saved.findings);
  actions.setFindingsChange(saved.findingsChange);
  actions.setFacilityNote(saved.facilityNote);
  actions.setProgressDownload(saved.progressDownload);
  actions.setEstimateDownload(saved.estimateDownload);
  actions.setIsDownloadingPdfReport(saved.isDownloadingPdfReport);
  actions.setOriginalDailySummaries(saved.originalDailySummaries);
  actions.setMinHrTabActive(saved.minHrTabActive);
  actions.setHrDataDefault(saved.hrDataDefault);
  actions.setDailySummariesCombine(saved.dailySummariesCombine);
  actions.setMaxHrsInDate(saved.maxHrsInDate);
  actions.setMinHrsInDate(saved.minHrsInDate);
  actions.setEcgBookmarkDeleteIds(saved.ecgBookmarkDeleteIds);
  actions.setReloadViewSizeConfig(saved.reloadViewSizeConfig);
  actions.setUpdatedAfibAvgHr(saved.updatedAfibAvgHr);
  actions.setSizeStripManagementThumbnailConfig(saved.sizeStripManagementThumbnailConfig);
  actions.setSizeStripDeviceThumbnailConfig(saved.sizeStripDeviceThumbnailConfig);
  actions.setSelectedEcgDisclosure(saved.selectedEcgDisclosure);
  actions.setEcgDisclosureChange(saved.ecgDisclosureChange);
  actions.setIsOpenInvalidTimeModal(saved.isOpenInvalidTimeModal);
  actions.setStripTotalStrip(saved.stripTotalStrip);
  actions.setTotalStripDevice(saved.totalStripDevice);
  actions.setIsSavingStripmanagement(saved.isSavingStripmanagement);
  actions.setSizeBeatThumbnailConfig(saved.sizeBeatThumbnailConfig);
  actions.setOriginalTemplateOverview(saved.originalTemplateOverview);
  actions.setInitTemplateOverview(saved.initTemplateOverview);
  actions.setTotalStrip(saved.totalStrip);
  actions.setListSelectedCells(saved.listSelectedCells);
  actions.setLoadingBeatStatus(saved.loadingBeatStatus);
  actions.setIsHideReviewedCell(saved.isHideReviewedCell);
  actions.setDataHeatmapChart(saved.dataHeatmapChart);
  actions.setDataHeatmapChartStatus(saved.dataHeatmapChartStatus);
  actions.setRrOverviewOrigin(saved.rrOverviewOrigin);
  actions.setCombinedEventsInHeatmap(saved.combinedEventsInHeatmap);
  actions.setViewBeatsSource(saved.viewBeatsSource);
  actions.setBulkReviewedBeats(saved.bulkReviewedBeats);
  actions.setReviewedBeatsChanges(saved.reviewedBeatsChanges);
  actions.setResetBulkReviewedBeats(saved.resetBulkReviewedBeats);
  actions.setVisibleReviewedFilter(saved.visibleReviewedFilter);
  actions.setBeatsSortType(saved.beatsSortType);
  actions.setIsLoadedIndexDB(saved.isLoadedIndexDB);
  actions.setReportInfo(saved.reportInfo);
  actions.setIsStartedAiProcessing(saved.isStartedAiProcessing);
  actions.setIsOpenProcessingLimit(saved.isOpenProcessingLimit);
  actions.setLimitProcessingStudy(saved.limitProcessingStudy);
  actions.setIsShowTabHeader(saved.isShowTabHeader);
  actions.setIsPushQueue(saved.isPushQueue);
  actions.setIsCompletedDownloadBeat(saved.isCompletedDownloadBeat);
  actions.setIsDownloadingBeatFile(saved.isDownloadingBeatFile);
  actions.setListDownloadFile(saved.listDownloadFile);
  actions.setProfileAfibAvgHr(saved.profileAfibAvgHr);
  actions.setIsDisabledBtn(saved.isDisabledBtn);
  actions.setIsUndoDisabled(saved.isUndoDisabled);
  actions.setIsRedoDisabled(saved.isRedoDisabled);
  actions.setDateList(saved.dateList);
  actions.setSpecialEventTypes(saved.specialEventTypes);
  actions.setIsRenderViewerEcg(saved.isRenderViewerEcg);
  actions.setSizeEventThumbnailConfig(saved.sizeEventThumbnailConfig);
  actions.setHighPassThumbnail(saved.highPassThumbnail);
  actions.setGainThumbnail(saved.gainThumbnail);
  actions.setChannelsThumbnail(saved.channelsThumbnail);
  actions.setEcgDataMap(saved.ecgDataMap);
  actions.setControlHrSummaryModal(saved.controlHrSummaryModal);
  actions.setRhythmEventCount(saved.rhythmEventCount);
  actions.setBeatEventCount(saved.beatEventCount);
  actions.setEcgBookmarks(saved.ecgBookmarks);
  actions.setUpdatedDailySummaries(saved.updatedDailySummaries);
  Object.keys(saved.viewHrMode).forEach((key) => actions.setViewHrMode(key, saved.viewHrMode[key]));
  Object.keys(current.viewHrMode)
    .filter((key) => !(key in saved.viewHrMode))
    .forEach((key) => actions.setViewHrMode(key, VIEW_HR_MODE_DEFAULT));
  Object.keys(saved.selectedHrTime).forEach((key) =>
    actions.setSelectedHrTime(key, saved.selectedHrTime[key]),
  );
  Object.keys(current.selectedHrTime)
    .filter((key) => !(key in saved.selectedHrTime))
    .forEach((key) => actions.setSelectedHrTime(key, undefined));
  Object.keys(saved.isViewNewEvent).forEach((key) =>
    actions.setIsViewNewEvent(key, saved.isViewNewEvent[key]),
  );
  Object.keys(current.isViewNewEvent)
    .filter((key) => !(key in saved.isViewNewEvent))
    .forEach((key) => actions.setIsViewNewEvent(key, IS_VIEW_NEW_EVENT_DEFAULT));
  Object.keys(saved.jumpDateStatus).forEach((key) =>
    actions.setJumpDateStatus(key, saved.jumpDateStatus[key]),
  );
  Object.keys(current.jumpDateStatus)
    .filter((key) => !(key in saved.jumpDateStatus))
    .forEach((key) => actions.setJumpDateStatus(key, JUMP_DATE_STATUS_DEFAULT));
  Object.keys(saved.chartHrsData).forEach((key) =>
    actions.setChartHrsData(key, saved.chartHrsData[key]),
  );
  Object.keys(current.chartHrsData)
    .filter((key) => !(key in saved.chartHrsData))
    .forEach((key) => actions.setChartHrsData(key, EMPTY_CHART_HRS_DATA));
  Object.keys(saved.eventStripsData).forEach((key) =>
    actions.setEventStripsData(key, saved.eventStripsData[key]),
  );
  Object.keys(current.eventStripsData)
    .filter((key) => !(key in saved.eventStripsData))
    .forEach((key) => actions.setEventStripsData(key, EMPTY_EVENT_STRIPS_DATA));
  Object.keys(saved.deviceEventData).forEach((key) =>
    actions.setDeviceEventData(key, saved.deviceEventData[key]),
  );
  Object.keys(current.deviceEventData)
    .filter((key) => !(key in saved.deviceEventData))
    .forEach((key) => actions.setDeviceEventData(key, DEVICE_EVENT_DATA_DEFAULT));
  Object.keys(saved.changeStripData).forEach((key) =>
    actions.setChangeStripData(key, saved.changeStripData[key]),
  );
  Object.keys(current.changeStripData)
    .filter((key) => !(key in saved.changeStripData))
    .forEach((key) => actions.setChangeStripData(key, EMPTY_CHANGE_STRIP_DATA));
  Object.keys(saved.stripFilter).forEach((key) =>
    actions.setStripFilter(key, saved.stripFilter[key]),
  );
  Object.keys(current.stripFilter)
    .filter((key) => !(key in saved.stripFilter))
    .forEach((key) => actions.setStripFilter(key, STRIP_FILTER_DEFAULT));
  Object.keys(saved.deviceStripFilter).forEach((key) =>
    actions.setDeviceStripFilter(key, saved.deviceStripFilter[key]),
  );
  Object.keys(current.deviceStripFilter)
    .filter((key) => !(key in saved.deviceStripFilter))
    .forEach((key) => actions.setDeviceStripFilter(key, DEVICE_STRIP_FILTER_DEFAULT));
  Object.keys(saved.emptySelectedKey).forEach((key) =>
    actions.setEmptySelectedKey(key, saved.emptySelectedKey[key]),
  );
  Object.keys(current.emptySelectedKey)
    .filter((key) => !(key in saved.emptySelectedKey))
    .forEach((key) => actions.setEmptySelectedKey(key, EMPTY_SELECTED_KEY_DEFAULT));
  Object.keys(saved.reloadStripManagement).forEach((key) =>
    actions.setReloadStripManagement(key, saved.reloadStripManagement[key]),
  );
  Object.keys(current.reloadStripManagement)
    .filter((key) => !(key in saved.reloadStripManagement))
    .forEach((key) => actions.setReloadStripManagement(key, RELOAD_STRIP_MANAGEMENT_DEFAULT));
  Object.keys(saved.deviceEventCount).forEach((key) =>
    actions.setDeviceEventCount(key, saved.deviceEventCount[key]),
  );
  Object.keys(current.deviceEventCount)
    .filter((key) => !(key in saved.deviceEventCount))
    .forEach((key) => actions.setDeviceEventCount(key, DEVICE_EVENT_COUNT_DEFAULT));
  Object.keys(saved.isWaiting).forEach((key) => actions.setIsWaiting(key, saved.isWaiting[key]));
  Object.keys(current.isWaiting)
    .filter((key) => !(key in saved.isWaiting))
    .forEach((key) => actions.setIsWaiting(key, IS_WAITING_DEFAULT));
  Object.keys(saved.isSkipSelectThumbnail).forEach((key) =>
    actions.setIsSkipSelectThumbnail(key, saved.isSkipSelectThumbnail[key]),
  );
  Object.keys(current.isSkipSelectThumbnail)
    .filter((key) => !(key in saved.isSkipSelectThumbnail))
    .forEach((key) => actions.setIsSkipSelectThumbnail(key, IS_SKIP_SELECT_THUMBNAIL_DEFAULT));
  Object.keys(saved.dailyArtifactCount).forEach((key) =>
    actions.setDailyArtifactCount(key, saved.dailyArtifactCount[key]),
  );
  Object.keys(current.dailyArtifactCount)
    .filter((key) => !(key in saved.dailyArtifactCount))
    .forEach((key) => actions.setDailyArtifactCount(key, EMPTY_DAILY_ARTIFACT_COUNT));
  Object.keys(saved.morphologyTemplate).forEach((key) =>
    actions.setMorphologyTemplate(key, saved.morphologyTemplate[key]),
  );
  Object.keys(current.morphologyTemplate)
    .filter((key) => !(key in saved.morphologyTemplate))
    .forEach((key) => actions.setMorphologyTemplate(key, EMPTY_MORPHOLOGY_TEMPLATE));
  Object.keys(saved.templateChanges).forEach((key) =>
    actions.setTemplateChanges(key, saved.templateChanges[key]),
  );
  Object.keys(current.templateChanges)
    .filter((key) => !(key in saved.templateChanges))
    .forEach((key) => actions.setTemplateChanges(key, EMPTY_TEMPLATE_CHANGES));
  Object.keys(saved.selectedTemplate).forEach((key) =>
    actions.setSelectedTemplate(key, saved.selectedTemplate[key]),
  );
  Object.keys(current.selectedTemplate)
    .filter((key) => !(key in saved.selectedTemplate))
    .forEach((key) => actions.setSelectedTemplate(key, SELECTED_TEMPLATE_DEFAULT));
  Object.keys(saved.swapBeatChanges).forEach((key) =>
    actions.setSwapBeatChanges(key, saved.swapBeatChanges[key]),
  );
  Object.keys(current.swapBeatChanges)
    .filter((key) => !(key in saved.swapBeatChanges))
    .forEach((key) => actions.setSwapBeatChanges(key, EMPTY_SWAP_BEAT_CHANGES));
  Object.keys(saved.bulkAction).forEach((key) => actions.setBulkAction(key, saved.bulkAction[key]));
  Object.keys(current.bulkAction)
    .filter((key) => !(key in saved.bulkAction))
    .forEach((key) => actions.setBulkAction(key, BULK_ACTION_DEFAULT));
  Object.keys(saved.bulkActionSwap).forEach((key) =>
    actions.setBulkActionSwap(key, saved.bulkActionSwap[key]),
  );
  Object.keys(current.bulkActionSwap)
    .filter((key) => !(key in saved.bulkActionSwap))
    .forEach((key) => actions.setBulkActionSwap(key, BULK_ACTION_SWAP_DEFAULT));
  Object.keys(saved.bulkSwapBeats).forEach((key) =>
    actions.setBulkSwapBeats(key, saved.bulkSwapBeats[key]),
  );
  Object.keys(current.bulkSwapBeats)
    .filter((key) => !(key in saved.bulkSwapBeats))
    .forEach((key) => actions.setBulkSwapBeats(key, EMPTY_BULK_SWAP_BEATS));
  Object.keys(saved.resetSwapBeats).forEach((key) =>
    actions.setResetSwapBeats(key, saved.resetSwapBeats[key]),
  );
  Object.keys(current.resetSwapBeats)
    .filter((key) => !(key in saved.resetSwapBeats))
    .forEach((key) => actions.setResetSwapBeats(key, EMPTY_RESET_SWAP_BEATS));
  Object.keys(saved.bulkActionByCell).forEach((key) =>
    actions.setBulkActionByCell(key, saved.bulkActionByCell[key]),
  );
  Object.keys(current.bulkActionByCell)
    .filter((key) => !(key in saved.bulkActionByCell))
    .forEach((key) => actions.setBulkActionByCell(key, BULK_ACTION_DEFAULT));
  Object.keys(saved.aiChannel).forEach((key) => actions.setAiChannel(key, saved.aiChannel[key]));
  Object.keys(current.aiChannel)
    .filter((key) => !(key in saved.aiChannel))
    .forEach((key) => actions.setAiChannel(key, AI_CHANNEL_DEFAULT));
  Object.keys(saved.ecgObject).forEach((key) => actions.setEcgObject(key, saved.ecgObject[key]));
  Object.keys(current.ecgObject)
    .filter((key) => !(key in saved.ecgObject))
    .forEach((key) => actions.setEcgObject(key, ECG_OBJECT_DEFAULT));
  Object.keys(saved.reloadEcgViewer).forEach((key) =>
    actions.setReloadEcgViewer(key, saved.reloadEcgViewer[key]),
  );
  Object.keys(current.reloadEcgViewer)
    .filter((key) => !(key in saved.reloadEcgViewer))
    .forEach((key) => actions.setReloadEcgViewer(key, RELOAD_ECG_VIEWER_DEFAULT));
  Object.keys(saved.isLoadingEcgViewer).forEach((key) =>
    actions.setIsLoadingEcgViewer(key, saved.isLoadingEcgViewer[key]),
  );
  Object.keys(current.isLoadingEcgViewer)
    .filter((key) => !(key in saved.isLoadingEcgViewer))
    .forEach((key) => actions.setIsLoadingEcgViewer(key, IS_LOADING_ECG_VIEWER_DEFAULT));
  Object.keys(saved.preventReloadEcg).forEach((key) =>
    actions.setPreventReloadEcg(key, saved.preventReloadEcg[key]),
  );
  Object.keys(current.preventReloadEcg)
    .filter((key) => !(key in saved.preventReloadEcg))
    .forEach((key) => actions.setPreventReloadEcg(key, PREVENT_RELOAD_ECG_DEFAULT));
  Object.keys(saved.pickDateAction).forEach((key) =>
    actions.setPickDateAction(key, saved.pickDateAction[key]),
  );
  Object.keys(current.pickDateAction)
    .filter((key) => !(key in saved.pickDateAction))
    .forEach((key) => actions.setPickDateAction(key, PICK_DATE_ACTION_DEFAULT));
  Object.keys(saved.pickedStartDate).forEach((key) =>
    actions.setPickedStartDate(key, saved.pickedStartDate[key]),
  );
  Object.keys(current.pickedStartDate)
    .filter((key) => !(key in saved.pickedStartDate))
    .forEach((key) => actions.setPickedStartDate(key, PICKED_START_DATE_DEFAULT));
  Object.keys(saved.pickedEndDate).forEach((key) =>
    actions.setPickedEndDate(key, saved.pickedEndDate[key]),
  );
  Object.keys(current.pickedEndDate)
    .filter((key) => !(key in saved.pickedEndDate))
    .forEach((key) => actions.setPickedEndDate(key, PICKED_END_DATE_DEFAULT));
  Object.keys(saved.pickedBookmarkDate).forEach((key) =>
    actions.setPickedBookmarkDate(key, saved.pickedBookmarkDate[key]),
  );
  Object.keys(current.pickedBookmarkDate)
    .filter((key) => !(key in saved.pickedBookmarkDate))
    .forEach((key) => actions.setPickedBookmarkDate(key, PICKED_BOOKMARK_DATE_DEFAULT));
  Object.keys(saved.jumpDate).forEach((key) => actions.setJumpDate(key, saved.jumpDate[key]));
  Object.keys(current.jumpDate)
    .filter((key) => !(key in saved.jumpDate))
    .forEach((key) => actions.setJumpDate(key, JUMP_DATE_DEFAULT));
  Object.keys(saved.jumpTime).forEach((key) => actions.setJumpTime(key, saved.jumpTime[key]));
  Object.keys(current.jumpTime)
    .filter((key) => !(key in saved.jumpTime))
    .forEach((key) => actions.setJumpTime(key, JUMP_TIME_DEFAULT));
  Object.keys(saved.jumpPortion).forEach((key) =>
    actions.setJumpPortion(key, saved.jumpPortion[key]),
  );
  Object.keys(current.jumpPortion)
    .filter((key) => !(key in saved.jumpPortion))
    .forEach((key) => actions.setJumpPortion(key, JUMP_PORTION_DEFAULT));
  Object.keys(saved.selectedDateValue).forEach((key) =>
    actions.setSelectedDateValue(key, saved.selectedDateValue[key]),
  );
  Object.keys(current.selectedDateValue)
    .filter((key) => !(key in saved.selectedDateValue))
    .forEach((key) => actions.setSelectedDateValue(key, SELECTED_DATE_VALUE_DEFAULT));
  Object.keys(saved.dailyCount).forEach((key) => actions.setDailyCount(key, saved.dailyCount[key]));
  Object.keys(current.dailyCount)
    .filter((key) => !(key in saved.dailyCount))
    .forEach((key) => actions.setDailyCount(key, DAILY_COUNT_DEFAULT));
  Object.keys(saved.activeTab).forEach((key) => actions.setActiveTab(key, saved.activeTab[key]));
  Object.keys(current.activeTab)
    .filter((key) => !(key in saved.activeTab))
    .forEach((key) => actions.setActiveTab(key, activeTabDefault(key)));
  Object.keys(saved.pageIndex).forEach((key) => actions.setPageIndex(key, saved.pageIndex[key]));
  Object.keys(current.pageIndex)
    .filter((key) => !(key in saved.pageIndex))
    .forEach((key) => actions.setPageIndex(key, PAGE_INDEX_DEFAULT));
  Object.keys(saved.isActiveTab).forEach((key) =>
    actions.setIsActiveTab(key, saved.isActiveTab[key]),
  );
  Object.keys(current.isActiveTab)
    .filter((key) => !(key in saved.isActiveTab))
    .forEach((key) => actions.setIsActiveTab(key, IS_ACTIVE_TAB_DEFAULT));
  Object.keys(saved.isEmptyData).forEach((key) =>
    actions.setIsEmptyData(key, saved.isEmptyData[key]),
  );
  Object.keys(current.isEmptyData)
    .filter((key) => !(key in saved.isEmptyData))
    .forEach((key) => actions.setIsEmptyData(key, IS_EMPTY_DATA_DEFAULT));
  Object.keys(saved.reloadHrChart).forEach((key) =>
    actions.setReloadHrChart(key, saved.reloadHrChart[key]),
  );
  Object.keys(current.reloadHrChart)
    .filter((key) => !(key in saved.reloadHrChart))
    .forEach((key) => actions.setReloadHrChart(key, RELOAD_HR_CHART_DEFAULT));
  Object.keys(saved.isLoadingHrChart).forEach((key) =>
    actions.setIsLoadingHrChart(key, saved.isLoadingHrChart[key]),
  );
  Object.keys(current.isLoadingHrChart)
    .filter((key) => !(key in saved.isLoadingHrChart))
    .forEach((key) => actions.setIsLoadingHrChart(key, IS_LOADING_HR_CHART_DEFAULT));
  Object.keys(saved.isLoadingHrChartOptions).forEach((key) =>
    actions.setIsLoadingHrChartOptions(key, saved.isLoadingHrChartOptions[key]),
  );
  Object.keys(current.isLoadingHrChartOptions)
    .filter((key) => !(key in saved.isLoadingHrChartOptions))
    .forEach((key) => actions.setIsLoadingHrChartOptions(key, IS_LOADING_HR_CHART_OPTIONS_DEFAULT));
  Object.keys(saved.eventFilter).forEach((key) =>
    actions.setEventFilter(key, saved.eventFilter[key]),
  );
  Object.keys(current.eventFilter)
    .filter((key) => !(key in saved.eventFilter))
    .forEach((key) => actions.setEventFilter(key, EVENT_FILTER_DEFAULT));
  Object.keys(saved.specialEventCount).forEach((key) =>
    actions.setSpecialEventCount(key, saved.specialEventCount[key]),
  );
  Object.keys(current.specialEventCount)
    .filter((key) => !(key in saved.specialEventCount))
    .forEach((key) => actions.setSpecialEventCount(key, SPECIAL_EVENT_COUNT_DEFAULT));
  Object.keys(saved.isShowArtifactEvent).forEach((key) =>
    actions.setIsShowArtifactEvent(key, saved.isShowArtifactEvent[key]),
  );
  Object.keys(current.isShowArtifactEvent)
    .filter((key) => !(key in saved.isShowArtifactEvent))
    .forEach((key) => actions.setIsShowArtifactEvent(key, IS_SHOW_ARTIFACT_EVENT_DEFAULT));
  Object.keys(saved.isShowAfibEvent).forEach((key) =>
    actions.setIsShowAfibEvent(key, saved.isShowAfibEvent[key]),
  );
  Object.keys(current.isShowAfibEvent)
    .filter((key) => !(key in saved.isShowAfibEvent))
    .forEach((key) => actions.setIsShowAfibEvent(key, IS_SHOW_AFIB_EVENT_DEFAULT));
  Object.keys(saved.isShowOtherEvent).forEach((key) =>
    actions.setIsShowOtherEvent(key, saved.isShowOtherEvent[key]),
  );
  Object.keys(current.isShowOtherEvent)
    .filter((key) => !(key in saved.isShowOtherEvent))
    .forEach((key) => actions.setIsShowOtherEvent(key, IS_SHOW_OTHER_EVENT_DEFAULT));
  Object.keys(saved.selectedStrip).forEach((key) =>
    actions.setSelectedStrip(key, saved.selectedStrip[key]),
  );
  Object.keys(current.selectedStrip)
    .filter((key) => !(key in saved.selectedStrip))
    .forEach((key) => actions.setSelectedStrip(key, SELECTED_STRIP_DEFAULT));
  Object.keys(saved.selectedMultipleStrip).forEach((key) =>
    actions.setSelectedMultipleStrip(key, saved.selectedMultipleStrip[key]),
  );
  Object.keys(current.selectedMultipleStrip)
    .filter((key) => !(key in saved.selectedMultipleStrip))
    .forEach((key) => actions.setSelectedMultipleStrip(key, SELECTED_MULTIPLE_STRIP_DEFAULT));
  Object.keys(saved.isLoadingStrip).forEach((key) =>
    actions.setIsLoadingStrip(key, saved.isLoadingStrip[key]),
  );
  Object.keys(current.isLoadingStrip)
    .filter((key) => !(key in saved.isLoadingStrip))
    .forEach((key) => actions.setIsLoadingStrip(key, IS_LOADING_STRIP_DEFAULT));
  Object.keys(saved.selectedColumnInfo).forEach((key) =>
    actions.setSelectedColumnInfo(key, saved.selectedColumnInfo[key]),
  );
  Object.keys(current.selectedColumnInfo)
    .filter((key) => !(key in saved.selectedColumnInfo))
    .forEach((key) => actions.setSelectedColumnInfo(key, SELECTED_COLUMN_INFO_DEFAULT));
  Object.keys(saved.prevColumnInfo).forEach((key) =>
    actions.setPrevColumnInfo(key, saved.prevColumnInfo[key]),
  );
  Object.keys(current.prevColumnInfo)
    .filter((key) => !(key in saved.prevColumnInfo))
    .forEach((key) => actions.setPrevColumnInfo(key, PREV_COLUMN_INFO_DEFAULT));
  Object.keys(saved.nextColumnInfo).forEach((key) =>
    actions.setNextColumnInfo(key, saved.nextColumnInfo[key]),
  );
  Object.keys(current.nextColumnInfo)
    .filter((key) => !(key in saved.nextColumnInfo))
    .forEach((key) => actions.setNextColumnInfo(key, NEXT_COLUMN_INFO_DEFAULT));
  Object.keys(saved.selectedEventIndex).forEach((key) =>
    actions.setSelectedEventIndex(key, saved.selectedEventIndex[key]),
  );
  Object.keys(current.selectedEventIndex)
    .filter((key) => !(key in saved.selectedEventIndex))
    .forEach((key) => actions.setSelectedEventIndex(key, SELECTED_EVENT_INDEX_DEFAULT));
  Object.keys(saved.selectedEvent).forEach((key) =>
    actions.setSelectedEvent(key, saved.selectedEvent[key]),
  );
  Object.keys(current.selectedEvent)
    .filter((key) => !(key in saved.selectedEvent))
    .forEach((key) => actions.setSelectedEvent(key, SELECTED_EVENT_DEFAULT));
  Object.keys(saved.selectedEventInfo).forEach((key) =>
    actions.setSelectedEventInfo(key, saved.selectedEventInfo[key]),
  );
  Object.keys(current.selectedEventInfo)
    .filter((key) => !(key in saved.selectedEventInfo))
    .forEach((key) => actions.setSelectedEventInfo(key, SELECTED_EVENT_INFO_DEFAULT));
  Object.keys(saved.selectedEventInfoIndex).forEach((key) =>
    actions.setSelectedEventInfoIndex(key, saved.selectedEventInfoIndex[key]),
  );
  Object.keys(current.selectedEventInfoIndex)
    .filter((key) => !(key in saved.selectedEventInfoIndex))
    .forEach((key) => actions.setSelectedEventInfoIndex(key, SELECTED_EVENT_INFO_INDEX_DEFAULT));
  Object.keys(saved.holterEvents).forEach((key) =>
    actions.setHolterEvents(key, saved.holterEvents[key]),
  );
  Object.keys(current.holterEvents)
    .filter((key) => !(key in saved.holterEvents))
    .forEach((key) => actions.setHolterEvents(key, HOLTER_EVENTS_DEFAULT));
  Object.keys(saved.combinedEvents).forEach((key) =>
    actions.setCombinedEvents(key, saved.combinedEvents[key]),
  );
  Object.keys(current.combinedEvents)
    .filter((key) => !(key in saved.combinedEvents))
    .forEach((key) => actions.setCombinedEvents(key, COMBINED_EVENTS_DEFAULT));
  Object.keys(saved.minuteInfoEvents).forEach((key) =>
    actions.setMinuteInfoEvents(key, saved.minuteInfoEvents[key]),
  );
  Object.keys(current.minuteInfoEvents)
    .filter((key) => !(key in saved.minuteInfoEvents))
    .forEach((key) => actions.setMinuteInfoEvents(key, MINUTE_INFO_EVENTS_DEFAULT));
  Object.keys(saved.isOpenModalEditStrip).forEach((key) =>
    actions.setIsOpenModalEditStrip(key, saved.isOpenModalEditStrip[key]),
  );
  Object.keys(current.isOpenModalEditStrip)
    .filter((key) => !(key in saved.isOpenModalEditStrip))
    .forEach((key) => actions.setIsOpenModalEditStrip(key, IS_OPEN_MODAL_EDIT_STRIP_DEFAULT));
  Object.keys(saved.controlModal).forEach((key) =>
    actions.setControlModal(key, saved.controlModal[key]),
  );
  Object.keys(current.controlModal)
    .filter((key) => !(key in saved.controlModal))
    .forEach((key) => actions.setControlModal(key, DEFAULT_CONTROL_MODAL_VALUE));
  Object.keys(saved.eventChanges).forEach((key) =>
    actions.setEventChanges(key, saved.eventChanges[key]),
  );
  Object.keys(current.eventChanges)
    .filter((key) => !(key in saved.eventChanges))
    .forEach((key) => actions.setEventChanges(key, EVENT_CHANGES_DEFAULT));
  Object.keys(saved.eventNews).forEach((key) => actions.setEventNews(key, saved.eventNews[key]));
  Object.keys(current.eventNews)
    .filter((key) => !(key in saved.eventNews))
    .forEach((key) => actions.setEventNews(key, EVENT_NEWS_DEFAULT));
  Object.keys(saved.eventOthers).forEach((key) =>
    actions.setEventOthers(key, saved.eventOthers[key]),
  );
  Object.keys(current.eventOthers)
    .filter((key) => !(key in saved.eventOthers))
    .forEach((key) => actions.setEventOthers(key, EVENT_OTHERS_DEFAULT));
  Object.keys(saved.beatChanges).forEach((key) =>
    actions.setBeatChanges(key, saved.beatChanges[key]),
  );
  Object.keys(current.beatChanges)
    .filter((key) => !(key in saved.beatChanges))
    .forEach((key) => actions.setBeatChanges(key, BEAT_CHANGES_DEFAULT));
  Object.keys(saved.groupEventChanges).forEach((key) =>
    actions.setGroupEventChanges(key, saved.groupEventChanges[key]),
  );
  Object.keys(current.groupEventChanges)
    .filter((key) => !(key in saved.groupEventChanges))
    .forEach((key) => actions.setGroupEventChanges(key, GROUP_EVENT_CHANGES_DEFAULT));
  Object.keys(saved.eventOptions).forEach((key) =>
    actions.setEventOptions(key, saved.eventOptions[key]),
  );
  Object.keys(current.eventOptions)
    .filter((key) => !(key in saved.eventOptions))
    .forEach((key) => actions.setEventOptions(key, DEFAULT_EVENT_OPTIONS));
  Object.keys(saved.beatOptions).forEach((key) =>
    actions.setBeatOptions(key, saved.beatOptions[key]),
  );
  Object.keys(current.beatOptions)
    .filter((key) => !(key in saved.beatOptions))
    .forEach((key) => actions.setBeatOptions(key, DEFAULT_BEAT_OPTIONS));
  Object.keys(saved.bulkBeatsChanges).forEach((key) =>
    actions.setBulkBeatsChanges(key, saved.bulkBeatsChanges[key]),
  );
  Object.keys(current.bulkBeatsChanges)
    .filter((key) => !(key in saved.bulkBeatsChanges))
    .forEach((key) => actions.setBulkBeatsChanges(key, BULK_BEATS_CHANGES_DEFAULT));
  Object.keys(saved.resetBulkBeatsChanges).forEach((key) =>
    actions.setResetBulkBeatsChanges(key, saved.resetBulkBeatsChanges[key]),
  );
  Object.keys(current.resetBulkBeatsChanges)
    .filter((key) => !(key in saved.resetBulkBeatsChanges))
    .forEach((key) => actions.setResetBulkBeatsChanges(key, RESET_BULK_BEATS_CHANGES_DEFAULT));
};
